import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useInventory } from '../context/InventoryContext';
import BulkUpload from '../components/BulkUpload';
import { useAuth } from '../context/AuthContext';
import { FaEdit, FaTrash, FaPlus, FaSearch, FaChevronDown, FaEye, FaTimes, FaChevronLeft, FaChevronRight, FaCamera, FaDownload, FaFileAlt, FaRedo, FaArrowLeft, FaWifi, FaSync } from 'react-icons/fa';
import Pagination from '../components/Pagination';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { fcrReportApi } from "../services/api";

// Signature Pad Component
const SignaturePad = ({ onSave, onClear, label, width = 300, height = 150, required = false }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
  }, []);

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches[0].clientX) - rect.left;
    const y = (e.clientY || e.touches[0].clientY) - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches[0].clientX) - rect.left;
    const y = (e.clientY || e.touches[0].clientY) - rect.top;
    
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    if (onClear) onClear();
  };

  const saveSignature = () => {
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL('image/png');
    if (onSave) onSave(dataUrl);
  };

  return (
    <div className="signature-pad">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <div className="border-2 border-gray-300 rounded-lg overflow-hidden relative bg-white">
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={() => { stopDrawing(); saveSignature(); }}
          onMouseLeave={() => { stopDrawing(); saveSignature(); }}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={() => { stopDrawing(); saveSignature(); }}
          className="touch-none w-full cursor-pen"
          style={{ touchAction: 'none' }}
        />
        {!hasSignature && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="text-gray-400 text-sm">Sign here</span>
          </div>
        )}
      </div>
      <div className="flex gap-2 mt-2">
        <button
          type="button"
          onClick={clearSignature}
          className="px-3 py-1 text-xs bg-red-500 hover:bg-red-600 text-white rounded-lg transition flex items-center gap-1"
        >
          <FaRedo size={10} /> Clear / Refresh
        </button>
      </div>
    </div>
  );
};

// Helper function to format dates as DD-MM-YYYY (no time)
const formatDateOnly = (dateString) => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  } catch (e) {
    return dateString;
  }
};

const VehicleDetails = () => {
  const {
    vehicles, addVehicle, updateVehicle, deleteVehicle,
    companies, devices, sims, subCompanies, getEntityDisplayName,
    updateDevice, updateSim, loadVehicles
  } = useInventory();

  const { currentUser, users } = useAuth();
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const isAdmin = currentUser?.role === 'admin';
  const isInstaller = currentUser?.role === 'installer';
  const isTeamLead = currentUser?.role === 'teamlead';

  const userCompanyId = currentUser?.companyId;
  const userSubCompanyId = currentUser?.subCompanyId;

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  const [vehicleTypeFilter, setVehicleTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [notification, setNotification] = useState({ message: '', type: '', visible: false });

  // ✅ NEW: Accessory Filter State
  const [selectedAccessories, setSelectedAccessories] = useState([]);
  const [accessoryFilterSearch, setAccessoryFilterSearch] = useState('');
  const [isAccessoryFilterOpen, setIsAccessoryFilterOpen] = useState(false);
  const accessoryFilterRef = useRef(null);

  // ✅ NEW: Installed By Filter State
  const [installedByFilter, setInstalledByFilter] = useState('');
  const [installedBySearch, setInstalledBySearch] = useState('');
  const [isInstalledByFilterOpen, setIsInstalledByFilterOpen] = useState(false);
  const installedByFilterRef = useRef(null);

  React.useEffect(() => {
    setCurrentPage(1);
  }, [isSuperAdmin, currentUser?.companyId]);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type, visible: true });
    setTimeout(() => {
      setNotification({ message: '', type: '', visible: false });
    }, 3000);
  };

  const [form, setForm] = useState({
    licensePlate: '',
    company: isInstaller ? (userSubCompanyId || userCompanyId || '') : '',
    imei: '',
    sim: '',
    vehicleType: 'bike',
    installerUsername: isInstaller ? currentUser.username : '',
    panicButtons: 0,
    accessories: [],
    images: [],
    category: ''
  });
  const [editingId, setEditingId] = useState(null);
  const [originalFormValues, setOriginalFormValues] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedParentId, setSelectedParentId] = useState('');
  const [selectedSubId, setSelectedSubId] = useState('');

  const [isImeiOpen, setIsImeiOpen] = useState(false);
  const [imeiSearchInput, setImeiSearchInput] = useState('');
  const [imeiActiveQuery, setImeiActiveQuery] = useState('');

  const [isSimOpen, setIsSimOpen] = useState(false);
  const [simSearchInput, setSimSearchInput] = useState('');
  const [simActiveQuery, setSimActiveQuery] = useState('');

  // SIM Status Check States
  const [simStatus, setSimStatus] = useState(null);
  const [isSimStatusLoading, setIsSimStatusLoading] = useState(false);
  const [showSimStatus, setShowSimStatus] = useState(false);

  const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);
  const [isUpdateMenuOpen, setIsUpdateMenuOpen] = useState(false);

  const [accessoryOptions, setAccessoryOptions] = useState(() => {
    const savedAccessories = localStorage.getItem('vehicleAccessories');
    if (savedAccessories) {
      try {
        return JSON.parse(savedAccessories);
      } catch (e) {
        return ['Immobilizer', 'Fuel Sensor', 'Door Buzzers'];
      }
    }
    return ['Immobilizer', 'Fuel Sensor', 'Door Buzzers'];
  });
  const [customAccessoryInput, setCustomAccessoryInput] = useState('');
  const [isAccessoryDropdownOpen, setIsAccessoryDropdownOpen] = useState(false);

  const [previewImages, setPreviewImages] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // State for installation report form
  const [showReportForm, setShowReportForm] = useState(false);
  const [reportData, setReportData] = useState({
    site: '',
    branch: '',
    reportNumber: '', 
    beforeInstallation: {
      wiperRight: false, wiperLeft: false,
      headLightRight: false, headLightLeft: false,
      fogLightRight: false, fogLightLeft: false,
      indicatorFrLeft: false, indicatorFrRight: false,
      indicatorBkLeft: false, indicatorBkRight: false,
      powerWindowFrLeft: false, powerWindowFrRight: false,
      powerWindowBkLeft: false, powerWindowBkRight: false,
      dashBoardConsole: false, musicSystem: false,
      parkingLight: false, centerLock: false,
      steering: false, horn: false, ac: false
    },
    afterInstallation: {
      wiperRight: false, wiperLeft: false,
      headLightRight: false, headLightLeft: false,
      fogLightRight: false, fogLightLeft: false,
      indicatorFrLeft: false, indicatorFrRight: false,
      indicatorBkLeft: false, indicatorBkRight: false,
      powerWindowFrLeft: false, powerWindowFrRight: false,
      powerWindowBkLeft: false, powerWindowBkRight: false,
      dashBoardConsole: false, musicSystem: false,
      parkingLight: false, centerLock: false,
      steering: false, horn: false, ac: false
    },
    customerRemarks: '',
    installerName: '',
    installerSignature: null,
    supervisorName: '',
    supervisorSignature: null,
    driverName: '',
    driverSignature: null,
    installationStartDate: new Date().toISOString().split('T')[0],
    installationEndDate: '',
    billingFromDate: new Date().toISOString().split('T')[0],
    newInstallation: false,
    reInstallation: false,
    removedDevice: false,
    replacementSim: false
  });
  const [reportErrors, setReportErrors] = useState({});
  const [viewingReport, setViewingReport] = useState(null);

  const imeiRef = useRef(null);
  const simRef = useRef(null);
  const accessoryRef = useRef(null);
  const addMenuRef = useRef(null);
  const updateMenuRef = useRef(null);
  const cameraInputRef = useRef(null);

  const [tempVehicleData, setTempVehicleData] = useState(null);

  // Category options - ONLY New installation and Re installation for adding
  const categoryOptions = [
    'New installation',
    'Re installation',
  ];

  const hasChanges = (original, current) => {
    if (!original) return false;
    
    const fieldsToCompare = ['licensePlate', 'company', 'imei', 'sim', 'vehicleType', 'installerUsername', 'panicButtons', 'category'];
    for (let field of fieldsToCompare) {
      const originalValue = (original[field] !== undefined && original[field] !== null) ? String(original[field]).trim() : '';
      const currentValue = (current[field] !== undefined && current[field] !== null) ? String(current[field]).trim() : '';
      if (originalValue !== currentValue) {
        return true;
      }
    }
    
    const originalAccessories = (original.accessories || []).sort().join(',');
    const currentAccessories = (current.accessories || []).sort().join(',');
    if (originalAccessories !== currentAccessories) {
      return true;
    }
    
    const originalImages = (original.images || []).length;
    const currentImages = (current.images || []).length;
    if (originalImages !== currentImages) {
      return true;
    }
    
    return false;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (imeiRef.current && !imeiRef.current.contains(event.target)) {
        setIsImeiOpen(false);
      }
      if (simRef.current && !simRef.current.contains(event.target)) {
        setIsSimOpen(false);
      }
      if (accessoryRef.current && !accessoryRef.current.contains(event.target)) {
        setIsAccessoryDropdownOpen(false);
      }
      // ✅ NEW: Close accessory filter dropdown when clicking outside
      if (accessoryFilterRef.current && !accessoryFilterRef.current.contains(event.target)) {
        setIsAccessoryFilterOpen(false);
      }
      // ✅ NEW: Close installed by filter dropdown when clicking outside
      if (installedByFilterRef.current && !installedByFilterRef.current.contains(event.target)) {
        setIsInstalledByFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (showForm || previewImages || showReportForm || viewingReport) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [showForm, previewImages, showReportForm, viewingReport]);

  const installers = users.filter(u => u.role === 'installer');

  const assignedInstallerIds = useMemo(() => {
    if (!isTeamLead) return null;
    return users
      .filter(u => u.role === 'installer' && String(u.teamLeadId) === String(currentUser.id))
      .map(u => u.id);
  }, [users, isTeamLead, currentUser]);

  const assignedInstallerUsernames = useMemo(() => {
    if (!isTeamLead) return null;
    return users
      .filter(u => u.role === 'installer' && String(u.teamLeadId) === String(currentUser.id))
      .map(u => u.username.toLowerCase());
  }, [users, isTeamLead, currentUser]);

  const allowedInstallerIds = installers.filter(inst => {
    if (isSuperAdmin) {
      if (selectedSubId) return String(inst.subCompanyId) === String(selectedSubId);
      if (selectedParentId) return String(inst.companyId) === String(selectedParentId);
      return true;
    }
    if (isAdmin) {
      if (String(inst.companyId) === String(currentUser.companyId)) return true;
      if (inst.subCompanyId) {
        const sub = subCompanies.find(s => String(s.id) === String(inst.subCompanyId));
        return sub && String(sub.parentCompanyId) === String(currentUser.companyId);
      }
      return false;
    }
    if (isTeamLead) {
      return assignedInstallerIds && assignedInstallerIds.includes(inst.id);
    }
    return false;
  });

  const subCompanyOptions = subCompanies.filter(
    s => String(s.parentCompanyId).trim() === String(selectedParentId).trim()
  );

  const assignableStatuses = ['new_stock', 'new stock', 'available', 'active', 'in use'];

  const availableDevices = devices.filter(d => {
    const statusOk = assignableStatuses.includes(d.status) ||
      (editingId && d.status === 'in use' && vehicles.find(v => v.id === editingId)?.imei === d.id);
    if (!statusOk) return false;
    if (selectedSubId) return String(d.companyId) === String(selectedSubId);
    if (selectedParentId) {
      return (
        String(d.parentCompanyId) === String(selectedParentId) ||
        String(d.companyId) === String(selectedParentId)
      );
    }
    return true;
  });

  const availableSims = sims.filter(s => {
    const statusOk = assignableStatuses.includes(s.status) ||
      (editingId && s.status === 'active' && vehicles.find(v => v.id === editingId)?.sim === s.id);
    if (!statusOk) return false;
    if (selectedSubId) return String(s.companyId) === String(selectedSubId);
    if (selectedParentId) {
      return (
        String(s.parentCompanyId) === String(selectedParentId) ||
        String(s.companyId) === String(selectedParentId)
      );
    }
    return true;
  });

  const handleImeiSearch = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setImeiActiveQuery(imeiSearchInput.trim().toLowerCase());
  };

  const handleSimSearch = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setSimActiveQuery(simSearchInput.trim().toLowerCase());
  };

  const searchedDevices = useMemo(() => {
    if (!imeiActiveQuery) return availableDevices;
    return availableDevices.filter(d => String(d.imei).toLowerCase().includes(imeiActiveQuery));
  }, [availableDevices, imeiActiveQuery]);

  const searchedSims = useMemo(() => {
    if (!simActiveQuery) return availableSims;
    return availableSims.filter(s => 
      String(s.simNo).toLowerCase().includes(simActiveQuery) || 
      String(s.mobileNo).toLowerCase().includes(simActiveQuery)
    );
  }, [availableSims, simActiveQuery]);

  const selectedDeviceObj = devices.find(d => String(d.id) === String(form.imei));
  const selectedSimObj = sims.find(s => String(s.id) === String(form.sim));

  // Function to check SIM status
  const checkSimStatus = useCallback(async () => {
    if (!form.sim) {
      toast.warning('Please select a SIM first');
      return;
    }

    setIsSimStatusLoading(true);
    try {
      const simObj = sims.find(s => String(s.id) === String(form.sim));
      if (!simObj) {
        toast.error('SIM not found');
        return;
      }

      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const currentStatus = simObj.status || 'available';
      
      let newStatus;
      if (currentStatus === 'active' || currentStatus === 'in use') {
        newStatus = 'active';
      } else if (currentStatus === 'available' || currentStatus === 'new_stock') {
        newStatus = Math.random() > 0.3 ? 'active' : 'available';
      } else {
        newStatus = currentStatus;
      }

      setSimStatus(newStatus);
      setShowSimStatus(true);
      
      if (newStatus === 'active') {
        toast.success('✅ SIM is active and ready for installation!');
      } else if (newStatus === 'available') {
        toast.info('SIM is available but not active yet. Click "Connect" to try connecting.');
      } else {
        toast.warning(`SIM status: ${newStatus}`);
      }
    } catch (error) {
      console.error('Error checking SIM status:', error);
      toast.error('Failed to check SIM status. Please try again.');
    } finally {
      setIsSimStatusLoading(false);
    }
  }, [form.sim, sims]);

  // Function to connect SIM
  const connectSim = useCallback(async () => {
    if (!form.sim) {
      toast.warning('Please select a SIM first');
      return;
    }

    setIsSimStatusLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const simObj = sims.find(s => String(s.id) === String(form.sim));
      if (!simObj) {
        toast.error('SIM not found');
        return;
      }

      const newStatus = 'active';
      setSimStatus(newStatus);
      setShowSimStatus(true);
      
      toast.success('✅ SIM connected successfully! Status: ACTIVE');
      
    } catch (error) {
      console.error('Error connecting SIM:', error);
      toast.error('Failed to connect SIM. Please try again.');
    } finally {
      setIsSimStatusLoading(false);
    }
  }, [form.sim, sims]);

  const resetForm = () => {
    setForm({
      licensePlate: '',
      company: isInstaller ? (userSubCompanyId || userCompanyId || '') : '',
      imei: '',
      sim: '',
      vehicleType: 'bike',
      installerUsername: isInstaller ? currentUser.username : '',
      panicButtons: 0,
      accessories: [],
      images: [],
      category: ''
    });
    setSelectedParentId('');
    setSelectedSubId('');
    setEditingId(null);
    setErrors({});
    setShowForm(false);
    setShowReportForm(false);
    setTempVehicleData(null);
    setViewingReport(null);
    setSimStatus(null);
    setShowSimStatus(false);
    
    setImeiSearchInput('');
    setImeiActiveQuery('');
    setIsImeiOpen(false);
    setSimSearchInput('');
    setSimActiveQuery('');
    setIsSimOpen(false);
    setCustomAccessoryInput('');
    setIsAccessoryDropdownOpen(false);
    
    setReportData({
      site: '',
      branch: '',
      beforeInstallation: {
        wiperRight: false, wiperLeft: false,
        headLightRight: false, headLightLeft: false,
        fogLightRight: false, fogLightLeft: false,
        indicatorFrLeft: false, indicatorFrRight: false,
        indicatorBkLeft: false, indicatorBkRight: false,
        powerWindowFrLeft: false, powerWindowFrRight: false,
        powerWindowBkLeft: false, powerWindowBkRight: false,
        dashBoardConsole: false, musicSystem: false,
        parkingLight: false, centerLock: false,
        steering: false, horn: false, ac: false
      },
      afterInstallation: {
        wiperRight: false, wiperLeft: false,
        headLightRight: false, headLightLeft: false,
        fogLightRight: false, fogLightLeft: false,
        indicatorFrLeft: false, indicatorFrRight: false,
        indicatorBkLeft: false, indicatorBkRight: false,
        powerWindowFrLeft: false, powerWindowFrRight: false,
        powerWindowBkLeft: false, powerWindowBkRight: false,
        dashBoardConsole: false, musicSystem: false,
        parkingLight: false, centerLock: false,
        steering: false, horn: false, ac: false
      },
      customerRemarks: '',
      installerName: '',
      installerSignature: null,
      supervisorName: '',
      supervisorSignature: null,
      driverName: '',
      driverSignature: null,
      installationStartDate: new Date().toISOString().split('T')[0],
      installationEndDate: '',
      billingFromDate: new Date().toISOString().split('T')[0],
      newInstallation: false,
      reInstallation: false,
      removedDevice: false,
      replacementSim: false
    });
    setReportErrors({});
  };

  const getDeviceImei = (id) => devices.find(d => String(d.id) === String(id))?.imei || '';
  const getSimNo = (id) => sims.find(s => String(s.id) === String(id))?.simNo || '';
  const getMobileNo = (id) => sims.find(s => String(s.id) === String(id))?.mobileNo || '';

  const getVehicleStatus = (v) => {
    const ignitionOn = v.ignition === true;
    const sosOn = v.sos === true;
    
    if (sosOn) return 'sos';
    if (ignitionOn) return 'ignition';
    return 'off';
  };

  // ✅ NEW: Filtered accessory options based on search
  const filteredAccessoryOptions = useMemo(() => {
    if (!accessoryFilterSearch.trim()) return accessoryOptions;
    return accessoryOptions.filter(option => 
      option.toLowerCase().includes(accessoryFilterSearch.toLowerCase())
    );
  }, [accessoryOptions, accessoryFilterSearch]);

  // ✅ NEW: Get all unique accessories from vehicles
  const allVehicleAccessories = useMemo(() => {
    const accessorySet = new Set();
    vehicles.forEach(v => {
      if (v.accessories && Array.isArray(v.accessories)) {
        v.accessories.forEach(acc => accessorySet.add(acc));
      }
    });
    return Array.from(accessorySet).sort();
  }, [vehicles]);

  // ✅ NEW: Toggle accessory selection
  const toggleAccessoryFilter = (accessory) => {
    setSelectedAccessories(prev => {
      if (prev.includes(accessory)) {
        return prev.filter(item => item !== accessory);
      } else {
        return [...prev, accessory];
      }
    });
  };

  // ✅ NEW: Clear all accessories
  const clearAccessoryFilters = () => {
    setSelectedAccessories([]);
    setAccessoryFilterSearch('');
  };

  // ✅ NEW: Get all unique installers from vehicles
  const allInstalledBy = useMemo(() => {
    const installerSet = new Set();
    vehicles.forEach(v => {
      if (v.installed_by) {
        installerSet.add(v.installed_by);
      }
    });
    return Array.from(installerSet).sort();
  }, [vehicles]);

  // ✅ NEW: Filtered installers based on search
  const filteredInstalledBy = useMemo(() => {
    if (!installedBySearch.trim()) return allInstalledBy;
    return allInstalledBy.filter(installer => 
      installer.toLowerCase().includes(installedBySearch.toLowerCase())
    );
  }, [allInstalledBy, installedBySearch]);

  const filteredVehicles = (isSuperAdmin
    ? vehicles
    : isInstaller
    ? vehicles.filter(v => String(v.installer_id) === String(currentUser.id))
    : isTeamLead
    ? vehicles.filter(v => {
        const installerId = v.installer_id;
        const installedBy = String(v.installed_by || '').toLowerCase();
        
        if (installerId && assignedInstallerIds && assignedInstallerIds.includes(installerId)) {
          return true;
        }
        
        if (installedBy && assignedInstallerUsernames && assignedInstallerUsernames.includes(installedBy)) {
          return true;
        }
        
        return false;
      })
    : vehicles.filter(v => {
        return (
          String(v.company) === String(currentUser.companyId) ||
          subCompanies.some(s => String(s.id) === String(v.company) && String(s.parentCompanyId) === String(currentUser.companyId))
        );
      })
  ).filter(v => {
    if (vehicleTypeFilter && v.vehicleType !== vehicleTypeFilter) return false;
    
    if (statusFilter) {
      const status = getVehicleStatus(v);
      if (status !== statusFilter) return false;
    }
    
    // ✅ NEW: Accessory filter
    if (selectedAccessories.length > 0) {
      const vehicleAccessories = (v.accessories || []).map(acc => acc.toLowerCase());
      const hasAllSelected = selectedAccessories.every(acc => 
        vehicleAccessories.includes(acc.toLowerCase())
      );
      if (!hasAllSelected) return false;
    }
    
    // ✅ NEW: Installed By filter
    if (installedByFilter) {
      const vehicleInstalledBy = String(v.installed_by || '').toLowerCase().trim();
      const filterInstalledBy = String(installedByFilter).toLowerCase().trim();
      if (vehicleInstalledBy !== filterInstalledBy) return false;
    }
    
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    const imei = getDeviceImei(v.imei);
    const simNo = getSimNo(v.sim);
    const mobileNo = getMobileNo(v.sim);
    return (
      v.licensePlate.toLowerCase().includes(q) ||
      imei.toLowerCase().includes(q) ||
      simNo.toLowerCase().includes(q) ||
      mobileNo.toLowerCase().includes(q)
    );
  });

  const totalItems = filteredVehicles.length;
  const paginatedVehicles = filteredVehicles.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const handleDownloadExcel = () => {
    const headers = [
      'License Plate', 'Company', 'IMEI', 'Mobile No', 'SIM No', 'Vehicle Type',
      'Panic Buttons', 'Accessories', 'Installed By', 'Category', 'Last Update', 'Status'
    ];

    const rows = filteredVehicles.map(v => [
      v.licensePlate || '',
      getEntityDisplayName(v.company) || '',
      getDeviceImei(v.imei) || '',
      getMobileNo(v.sim) || '',
      getSimNo(v.sim) || '',
      v.vehicleType || '',
      v.panic_buttons || v.panicButtons || 0,
      (v.accessories && v.accessories.length > 0) ? v.accessories.join(', ') : '',
      v.installed_by || '',
      v.category || '',
      v.last_update || v.updatedAt || v.createdAt || '-',
      v.status || '-'
    ]);

    const csvContent = [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `Vehicle_Details_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!form.licensePlate.trim()) {
      newErrors.licensePlate = 'License plate is required.';
    } else {
      const duplicate = vehicles.find(
        v => v.id !== editingId && v.licensePlate.toLowerCase() === form.licensePlate.trim().toLowerCase()
      );
      if (duplicate) newErrors.licensePlate = 'License plate already exists.';
    }
    if (!form.imei) newErrors.imei = 'Please select a device (IMEI).';
    if (!form.sim) newErrors.sim = 'Please select a SIM.';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateReport = () => {
    const newErrors = {};
    if (!reportData.site.trim()) {
      newErrors.site = 'Site is required.';
    }
    if (!reportData.branch.trim()) {
      newErrors.branch = 'Branch is required.';
    }
    if (!reportData.installerSignature) {
      newErrors.installerSignature = 'Installer signature is required.';
    }
    if (!reportData.supervisorSignature) {
      newErrors.supervisorSignature = 'Supervisor signature is required.';
    }
    if (!reportData.driverSignature) {
      newErrors.driverSignature = 'Driver signature is required.';
    }

    setReportErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    
    setForm(prev => ({
      ...prev,
      images: [...prev.images, ...files]
    }));
  };

  // ✅ FIXED: Camera capture - Convert file to data URL to prevent white screen on mobile
  const handleCameraCapture = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const imageDataUrl = event.target.result;
        setForm(prev => ({
          ...prev,
          images: [...prev.images, imageDataUrl]
        }));
      };
      reader.readAsDataURL(file);
    }
    if (cameraInputRef.current) {
      cameraInputRef.current.value = '';
    }
  };

  const removeSelectedFormImage = (indexToRemove) => {
    setForm(prev => ({
      ...prev,
      images: prev.images.filter((_, idx) => idx !== indexToRemove)
    }));
  };

  const handleReportCheckboxChange = (section, field) => {
    setReportData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: !prev[section][field]
      }
    }));
  };

  const handleSignatureSave = (type, signatureData) => {
    setReportData(prev => ({
      ...prev,
      [type]: signatureData
    }));
    if (reportErrors[type]) {
      setReportErrors(prev => ({ ...prev, [type]: '' }));
    }
  };

  const handleSignatureClear = (type) => {
    setReportData(prev => ({
      ...prev,
      [type]: null
    }));
  };

  // ✅ FIXED: Format dates to DD-MM-YYYY when viewing report
  const handleViewReport = async (vehicle) => {
    try {
      if (vehicle.id) {
        console.log('🔍 Fetching FCR for vehicle ID:', vehicle.id);
        const response = await fcrReportApi.getByVehicle(vehicle.id);
        
        if (response.data.success && response.data.data) {
          const reportData = response.data.data;
          
          // Format dates to DD-MM-YYYY before displaying
          const formattedReport = {
            ...reportData,
            installationStartDate: formatDateOnly(reportData.installationStartDate || reportData.installation_start_date),
            installationEndDate: formatDateOnly(reportData.installationEndDate || reportData.installation_end_date),
            billingFromDate: formatDateOnly(reportData.billingFromDate || reportData.billing_from_date),
            createdAt: formatDateOnly(reportData.createdAt || reportData.created_at)
          };
          
          setViewingReport(formattedReport);
          toast.success('✅ FCR report loaded successfully!');
        } else {
          toast.info('No FCR report available for this vehicle');
        }
      } else {
        toast.info('No FCR report available for this vehicle');
      }
    } catch (error) {
      console.error('Error fetching FCR report:', error);
      if (error.response?.status === 404) {
        toast.info('No FCR report found for this vehicle');
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view FCR reports');
      } else {
        toast.error('Failed to fetch FCR report');
      }
    }
  };

  // ✅ FIXED: Use formatDateOnly in download report
  const handleDownloadReport = (reportDataToDownload) => {
    const report = reportDataToDownload || viewingReport || reportData;
    
    const formatFieldName = (key) => {
      return key.replace(/([A-Z])/g, ' $1').trim();
    };

    const getCheckboxHTML = (section) => {
      const entries = Object.entries(section || {});
      const mid = Math.ceil(entries.length / 2);
      const col1 = entries.slice(0, mid);
      const col2 = entries.slice(mid);
      
      return `
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 2px 20px; padding: 4px 8px;">
          ${col1.map(([key, value]) => `
            <div style="display: flex; align-items: center; gap: 4px; font-size: 10px; padding: 1px 0;">
              <span style="font-size: 12px; color: ${value ? '#22c55e' : '#999'}; width: 16px;">${value ? '✓' : '□'}</span>
              <span style="white-space: nowrap;">${formatFieldName(key)}</span>
            </div>
          `).join('')}
          ${col2.map(([key, value]) => `
            <div style="display: flex; align-items: center; gap: 4px; font-size: 10px; padding: 1px 0;">
              <span style="font-size: 12px; color: ${value ? '#22c55e' : '#999'}; width: 16px;">${value ? '✓' : '□'}</span>
              <span style="white-space: nowrap;">${formatFieldName(key)}</span>
            </div>
          `).join('')}
        </div>
      `;
    };

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Field Call Report</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: Arial, Helvetica, sans-serif; 
              padding: 15px 20px; 
              background: white; 
              color: #333;
              font-size: 11px;
            }
            .report-container { 
              max-width: 1100px; 
              margin: 0 auto; 
            }
            
            .header { 
              display: flex;
              align-items: center;
              gap: 15px;
              border-bottom: 2px solid #333;
              padding-bottom: 8px;
              margin-bottom: 10px;
            }
            .header-logo { height: 50px; width: auto; }
            .header-text h1 { font-size: 18px; }
            .header-text h2 { font-size: 13px; font-weight: normal; color: #555; }
            .header-text .report-no { font-size: 11px; color: #666; }
            
            .row { display: flex; gap: 15px; margin-bottom: 6px; }
            .col { flex: 1; }
            .col-2 { flex: 2; }
            .col-3 { flex: 3; }
            
            .field { margin-bottom: 3px; }
            .field label { font-weight: bold; font-size: 10px; color: #555; display: block; }
            .field .value { 
              padding: 2px 6px; 
              background: #f9f9f9; 
              border: 1px solid #eee; 
              border-radius: 2px;
              font-size: 11px;
              min-height: 22px;
            }
            
            .section-title { 
              background: #f0f0f0; 
              padding: 3px 10px; 
              font-weight: bold; 
              border: 1px solid #ddd;
              font-size: 11px;
              margin-top: 6px;
            }
            
            .status-row {
              display: flex;
              flex-wrap: wrap;
              gap: 10px 20px;
              padding: 4px 10px;
              border: 1px solid #ddd;
              background: #fafafa;
              margin: 4px 0;
              align-items: center;
            }
            .status-item {
              display: flex;
              align-items: center;
              gap: 4px;
              font-size: 10px;
            }
            .status-item .check { font-size: 12px; }
            .status-item .check.checked { color: #22c55e; }
            
            .vehicle-details {
              display: grid;
              grid-template-columns: 1fr 1fr 1fr;
              gap: 10px;
              padding: 6px 10px;
              border: 1px solid #ddd;
              background: #f0f7ff;
              margin: 4px 0;
            }
            .vehicle-details .field label { color: #1a3a6b; }
            .vehicle-details .field .value { 
              background: white; 
              font-weight: bold;
              font-size: 12px;
              color: #1a3a6b;
            }
            
            .remarks {
              padding: 4px 10px;
              border: 1px solid #ddd;
              min-height: 30px;
              background: #fafafa;
              margin: 4px 0;
              font-size: 10px;
            }
            
            .signatures {
              display: grid;
              grid-template-columns: 1fr 1fr 1fr;
              gap: 12px;
              border-top: 2px solid #333;
              padding-top: 10px;
              margin-top: 10px;
            }
            .sig-box {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: center;
              background: #fafafa;
              min-height: 60px;
            }
            .sig-box .label { font-weight: bold; font-size: 10px; display: block; }
            .sig-box .name { font-size: 10px; color: #555; margin: 3px 0; }
            .sig-img { max-width: 120px; max-height: 40px; border: 1px solid #eee; padding: 2px; background: white; }
            .sig-placeholder { color: #999; font-style: italic; font-size: 10px; }
            
            .footer { 
              text-align: center; 
              margin-top: 10px; 
              padding-top: 6px; 
              border-top: 1px solid #ddd; 
              font-size: 9px; 
              color: #999;
            }
            
            @media print {
              body { padding: 10px 15px; }
              .sig-box { break-inside: avoid; }
            }

            @media screen and (max-width: 768px) {
              body { padding: 10px; font-size: 10px; }
              .header { flex-direction: column; gap: 8px; text-align: center; }
              .header-text h1 { font-size: 16px; }
              .header-text h2 { font-size: 12px; }
              .row { flex-direction: column; gap: 6px; }
              .col { width: 100%; }
              .vehicle-details { grid-template-columns: 1fr; gap: 6px; }
              .signatures { grid-template-columns: 1fr; gap: 10px; }
              .status-row { flex-direction: column; align-items: flex-start; gap: 4px; }
              .status-item { width: 100%; }
              .sig-box { min-height: 50px; }
              .sig-img { max-width: 100px; max-height: 35px; }
              .report-container { padding: 0 5px; }
              .section-title { font-size: 10px; padding: 2px 8px; }
              .field .value { font-size: 10px; padding: 2px 4px; }
            }

            @media screen and (max-width: 480px) {
              body { padding: 5px; font-size: 9px; }
              .header-text h1 { font-size: 14px; }
              .header-text h2 { font-size: 10px; }
              .header-logo { height: 35px; }
              .vehicle-details .field .value { font-size: 10px; }
              .status-item { font-size: 9px; }
              .sig-box .label { font-size: 9px; }
              .sig-box .name { font-size: 9px; }
            }
          </style>
        </head>
        <body>
          <div class="report-container">
            
            <div class="header">
              <img src="https://cccs.cordonnx.com/Loginlogo.png" alt="Cordon Logo" class="header-logo" />
              <div class="header-text">
                <h1>Cordon Telematics Pvt. Ltd.</h1>
                <h2>Installation report / Field Call Report</h2>
                <div class="report-no">No. ${reportDataToDownload.reportNumber || reportDataToDownload.report_number || 'N/A'}</div>
              </div>
            </div>

            <div class="row">
              <div class="col">
                <div class="field">
                  <label>Site</label>
                  <div class="value">${report.site || '-'}</div>
                </div>
              </div>
              <div class="col">
                <div class="field">
                  <label>Branch</label>
                  <div class="value">${report.branch || '-'}</div>
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col">
                <div class="field">
                  <label>Installation Start Date</label>
                  <div class="value">${formatDateOnly(report.installationStartDate || report.installation_start_date)}</div>
                </div>
              </div>
              <div class="col">
                <div class="field">
                  <label>Installation End Date</label>
                  <div class="value">${formatDateOnly(report.installationEndDate || report.installation_end_date)}</div>
                </div>
              </div>
              <div class="col">
                <div class="field">
                  <label>Billing From Date</label>
                  <div class="value">${formatDateOnly(report.billingFromDate || report.billing_from_date)}</div>
                </div>
              </div>
            </div>

            <div class="row" style="gap: 10px;">
              <div class="col" style="flex: 1;">
                <div class="section-title">BEFORE INSTALLATION</div>
                ${getCheckboxHTML(report.beforeInstallation || report.before_installation || {})}
              </div>
              <div class="col" style="flex: 1;">
                <div class="section-title">AFTER INSTALLATION</div>
                ${getCheckboxHTML(report.afterInstallation || report.after_installation || {})}
              </div>
            </div>

            <div class="status-row">
              <div class="status-item">
                <span class="check ${report.removedDevice || report.removed_device ? 'checked' : ''}">${report.removedDevice || report.removed_device ? '✓' : '□'}</span>
                <span>Removed Device</span>
              </div>
              <div class="status-item">
                <span class="check ${report.replacementSim || report.replacement_sim ? 'checked' : ''}">${report.replacementSim || report.replacement_sim ? '✓' : '□'}</span>
                <span>Replacement Sim</span>
              </div>
            </div>

            <div class="vehicle-details">
              <div class="field">
                <label>Vehicle No.</label>
                <div class="value">${report.vehicleNumber || report.vehicle_number || (tempVehicleData?.licensePlate) || '-'}</div>
              </div>
              <div class="field">
                <label>IMEI No.</label>
                <div class="value">${report.imei || (tempVehicleData?.imei ? getDeviceImei(tempVehicleData.imei) : '') || '-'}</div>
              </div>
              <div class="field">
                <label>SIM No.</label>
                <div class="value">${report.simNo || report.sim_no || (tempVehicleData?.sim ? getSimNo(tempVehicleData.sim) : '') || '-'}</div>
              </div>
            </div>

            <div>
              <label style="font-weight: bold; font-size: 10px; display: block;">Customer Remarks</label>
              <div class="remarks">${report.customerRemarks || report.customer_remarks || '-'}</div>
            </div>

            <div class="signatures">
              <div class="sig-box">
                <span class="label">Installed By</span>
                <div class="name">${report.installerName || report.installer_name || '-'}</div>
                ${report.installerSignature ? 
                  `<img src="${report.installerSignature}" class="sig-img" />` :
                  '<div class="sig-placeholder">No signature</div>'
                }
              </div>
              <div class="sig-box">
                <span class="label">Supervisor / Admin</span>
                <div class="name">${report.supervisorName || report.supervisor_name || '-'}</div>
                ${report.supervisorSignature ? 
                  `<img src="${report.supervisorSignature}" class="sig-img" />` :
                  '<div class="sig-placeholder">No signature</div>'
                }
              </div>
              <div class="sig-box">
                <span class="label">Driver</span>
                <div class="name">${report.driverName || report.driver_name || '-'}</div>
                ${report.driverSignature ? 
                  `<img src="${report.driverSignature}" class="sig-img" />` :
                  '<div class="sig-placeholder">No signature</div>'
                }
              </div>
            </div>

            <div class="footer">Generated on ${formatDateOnly(new Date().toISOString())}</div>
            
          </div>
          <script>
            window.onload = function() { 
              setTimeout(function() { window.print(); }, 300);
            }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    let finalCompany;
    if (isInstaller) finalCompany = userSubCompanyId || userCompanyId;
    else if (isSuperAdmin) finalCompany = form.company;
    else finalCompany = form.company || currentUser.companyId;

    const selectedInstaller = form.installerUsername
      ? installers.find(inst => inst.username === form.installerUsername)
      : null;

    const data = {
      ...form,
      company: finalCompany || '',
      company_id: finalCompany || null,
      imei: form.imei || '',
      device_id: form.imei || null,
      sim: form.sim || '',
      sim_id: form.sim || null,
      vehicle_number: form.licensePlate || '',
      vehicle_type: form.vehicleType || 'bike',
      panic_buttons: Number(form.panicButtons),
      installed_by: isInstaller
        ? currentUser.username
        : (form.installerUsername || ''),
      installer_id: isInstaller
        ? currentUser.id
        : (selectedInstaller?.id || null),
      accessories: form.accessories || [],
      images: form.images || [],
      category: form.category || ''
    };

    if (editingId) {
      if (!hasChanges(originalFormValues, data)) {
        setErrors({});
        resetForm();
        toast.info('No changes were made to the vehicle.');
        return;
      }
      
      updateVehicle(editingId, data);
      toast.success(`✅ Vehicle "${form.licensePlate.trim()}" has been updated successfully!`);
      resetForm();
    } else {
      setTempVehicleData(data);
      setShowForm(false);
      setShowReportForm(true);
      
      setReportData(prev => ({
        ...prev,
        site: finalCompany ? getEntityDisplayName(finalCompany) : '',
        branch: finalCompany ? (companies.find(c => String(c.id) === String(finalCompany))?.address || '') : '',
        installerName: isInstaller ? currentUser.username : (form.installerUsername || ''),
        installationStartDate: new Date().toISOString().split('T')[0],
        newInstallation: form.category === 'New installation',
        reInstallation: form.category === 'Re installation',
        billingFromDate: new Date().toISOString().split('T')[0]
      }));
    }
  };

  const handleReportSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateReport()) {
        toast.error('Please fill all required fields and add all signatures.');
        return;
    }
    
    try {
        if (tempVehicleData) {
            console.log('🚗 Creating vehicle with data:', tempVehicleData);
            
            const vehicleData = {
                licensePlate: tempVehicleData.licensePlate,
                company: tempVehicleData.company,
                imei: tempVehicleData.imei,
                sim: tempVehicleData.sim,
                vehicleType: tempVehicleData.vehicleType || 'bike',
                installerUsername: tempVehicleData.installerUsername,
                panicButtons: Number(tempVehicleData.panicButtons) || 0,
                accessories: tempVehicleData.accessories || [],
                images: tempVehicleData.images || [],
                category: tempVehicleData.category || '',
                installer_id: tempVehicleData.installer_id || null,
                vehicle_number: tempVehicleData.licensePlate,
                company_id: tempVehicleData.company,
                device_id: tempVehicleData.imei,
                sim_id: tempVehicleData.sim,
                vehicle_type: tempVehicleData.vehicleType || 'bike',
                panic_buttons: Number(tempVehicleData.panicButtons) || 0,
                status: 'active'
            };

            const vehicleResponse = await addVehicle(vehicleData);
            console.log('✅ Vehicle created:', vehicleResponse);
            
            const vehicleId = vehicleResponse?.id || vehicleResponse?.data?.id;
            console.log('📝 Vehicle ID:', vehicleId);
            
            if (vehicleId) {
                const fcrData = {
                    site: reportData.site,
                    branch: reportData.branch,
                    beforeInstallation: reportData.beforeInstallation,
                    afterInstallation: reportData.afterInstallation,
                    customerRemarks: reportData.customerRemarks,
                    installerName: reportData.installerName,
                    installerSignature: reportData.installerSignature,
                    supervisorName: reportData.supervisorName,
                    supervisorSignature: reportData.supervisorSignature,
                    driverName: reportData.driverName,
                    driverSignature: reportData.driverSignature,
                    installationStartDate: reportData.installationStartDate || new Date().toISOString().split('T')[0],
                    installationEndDate: reportData.installationEndDate || null,
                    billingFromDate: reportData.billingFromDate || new Date().toISOString().split('T')[0],
                    newInstallation: reportData.newInstallation || false,
                    reInstallation: reportData.reInstallation || false,
                    removedDevice: reportData.removedDevice || false,
                    replacementSim: reportData.replacementSim || false
                };
                
                console.log('📋 Creating FCR with data:', fcrData);
                console.log('🔗 Vehicle ID for FCR:', vehicleId);
                
                const fcrResponse = await fcrReportApi.create(vehicleId, fcrData);
                console.log('✅ FCR created:', fcrResponse.data);
                
                await loadVehicles();
                
                toast.success('✅ Vehicle and FCR report saved successfully!');
                resetForm();
            } else {
                console.error('❌ No vehicle ID returned from addVehicle');
                toast.error('Failed to get vehicle ID after creation.');
            }
        }
    } catch (error) {
        console.error('❌ Error saving vehicle with FCR:', error);
        console.error('Error response data:', error.response?.data);
        console.error('Error response status:', error.response?.status);
        const errorMessage = error.response?.data?.message || 'Failed to save vehicle with FCR report. Please try again.';
        toast.error(errorMessage);
    }
};

  const handleReportSkip = async () => {
    try {
      if (tempVehicleData) {
        const vehicleData = {
          licensePlate: tempVehicleData.licensePlate,
          company: tempVehicleData.company,
          imei: tempVehicleData.imei,
          sim: tempVehicleData.sim,
          vehicleType: tempVehicleData.vehicleType,
          installerUsername: tempVehicleData.installerUsername,
          panicButtons: tempVehicleData.panicButtons || 0,
          accessories: tempVehicleData.accessories || [],
          images: tempVehicleData.images || [],
          category: tempVehicleData.category || '',
          vehicle_number: tempVehicleData.licensePlate,
          company_id: tempVehicleData.company,
          device_id: tempVehicleData.imei,
          sim_id: tempVehicleData.sim,
          vehicle_type: tempVehicleData.vehicleType,
          panic_buttons: tempVehicleData.panicButtons || 0,
          installer_id: tempVehicleData.installer_id || null
        };

        await addVehicle(vehicleData);
        
        await loadVehicles();
        
        toast.success(`✅ Vehicle "${tempVehicleData.licensePlate}" has been added successfully!`);
      }
      resetForm();
    } catch (error) {
      console.error('Error saving vehicle:', error);
      const errorMessage = error.response?.data?.message || 'Failed to save vehicle. Please try again.';
      toast.error(errorMessage);
    }
  };

  const handleGoBack = () => {
    setShowReportForm(false);
    setShowForm(true);
    if (tempVehicleData) {
      setForm({
        licensePlate: tempVehicleData.licensePlate || '',
        company: tempVehicleData.company || '',
        imei: tempVehicleData.imei || '',
        sim: tempVehicleData.sim || '',
        vehicleType: tempVehicleData.vehicleType || 'bike',
        installerUsername: tempVehicleData.installerUsername || '',
        panicButtons: tempVehicleData.panic_buttons || 0,
        accessories: tempVehicleData.accessories || [],
        images: tempVehicleData.images || [],
        category: tempVehicleData.category || ''
      });
    }
  };

  const handleEdit = (vehicle) => {
    const savedCompanyId = String(vehicle.company || '');
    let parentId = '';
    let subId = '';
    
    const sub = subCompanies.find(s => String(s.id) === savedCompanyId);
    if (sub) {
      parentId = String(sub.parentCompanyId);
      subId = String(sub.id);
    } else {
      parentId = savedCompanyId;
    }

    const initialData = {
      licensePlate: vehicle.licensePlate || '',
      company: subId || parentId,
      company_id: subId || parentId || null,
      imei: String(vehicle.imei || ''),
      device_id: String(vehicle.imei || ''),
      sim: String(vehicle.sim || ''),
      sim_id: String(vehicle.sim || ''),
      vehicleType: vehicle.vehicleType || 'bike',
      vehicle_type: vehicle.vehicleType || 'bike',
      vehicle_number: vehicle.licensePlate || '',
      installerUsername: vehicle.installed_by || '',
      panicButtons: vehicle.panic_buttons ? Number(vehicle.panic_buttons) : 0,
      panic_buttons: vehicle.panic_buttons ? Number(vehicle.panic_buttons) : 0,
      accessories: vehicle.accessories || [],
      images: vehicle.images || [],
      category: vehicle.category || ''
    };

    setForm(initialData);
    setOriginalFormValues(initialData);
    
    setSelectedParentId(parentId);
    setSelectedSubId(subId);
    setEditingId(vehicle.id);
    setShowForm(true);
  };

  const handleDelete = (vehicleId) => {
    const vehicleToDelete = vehicles.find(v => v.id === vehicleId);
    if (vehicleToDelete && window.confirm(`Are you sure you want to delete vehicle "${vehicleToDelete.licensePlate}"?`)) {
      deleteVehicle(vehicleId);
      showNotification(`Vehicle "${vehicleToDelete.licensePlate}" has been deleted`, 'error');
    }
  };

  // FIXED: Simplified validateRow - ONLY checks if license plate is provided
  // FIXED: validateRow now catches column-shift errors (IMEI/SIM/vehicle type)
const validateRow = (row, index, allRows) => {
  const errs = [];
  const plate = (row.licensePlate || '').trim();

  if (!plate) {
    errs.push({ column: 'License Plate', message: 'Required' });
  } else {
    // Check if license plate already exists in database
    if (vehicles.some(v => v.licensePlate.toLowerCase() === plate.toLowerCase())) {
      errs.push({ column: 'License Plate', message: 'License plate already exists' });
    }
    // Check for duplicates within the file
    const earlierDuplicate = allRows.slice(0, index).some(
      r => (r.licensePlate || '').trim().toLowerCase() === plate.toLowerCase()
    );
    if (earlierDuplicate) {
      errs.push({ column: 'License Plate', message: 'Duplicate license plate in file' });
    }
  }

  // IMEI must exist in devices
  const imei = (row.imei || '').toString().trim();
  if (imei) {
    const deviceExists = devices.some(d => String(d.imei).trim() === imei);
    if (!deviceExists) {
      errs.push({ column: 'IMEI', message: `IMEI "${imei}" not found in Device Inventory` });
    }
  } else {
    errs.push({ column: 'IMEI', message: 'IMEI is required' });
  }

  // SIM must exist in sims
  const simNo = (row.simNo || '').toString().trim();
  if (simNo) {
    const simExists = sims.some(s => String(s.simNo).trim() === simNo);
    if (!simExists) {
      errs.push({ column: 'SIM No', message: `SIM "${simNo}" not found in SIM Inventory` });
    }
  } else {
    errs.push({ column: 'SIM No', message: 'SIM No is required' });
  }

  // Vehicle type must be valid
  const vt = (row.vehicleType || '').toString().trim().toLowerCase();
  if (vt && !['bike','bus','truck','hatchback','sedan','suv','van','car'].includes(vt)) {
    errs.push({ column: 'Vehicle Type', message: `Invalid vehicle type "${vt}"` });
  }

  console.log('Validation errors:', errs);
  return errs;
};

 // FIXED: Bulk add handler with proper chunk handling
const handleBulkAdd = async (rows) => {
  console.log(`📥 Processing chunk of ${rows.length} vehicles`);
  
  let addedCount = 0;
  let skippedCount = 0;
  const errors = [];
  
  for (const row of rows) {
    try {
      const plate = (row.licensePlate || '').toString().trim();
      if (!plate) {
        skippedCount++;
        errors.push(`License plate is required`);
        return;
      }

      // Check if license plate already exists
      if (vehicles.some(v => v.licensePlate.toLowerCase() === plate.toLowerCase())) {
        skippedCount++;
        errors.push(`License plate "${plate}" already exists`);
        return;
      }

      // Find device by IMEI
      const imei = (row.imei || '').toString().trim();
      const device = devices.find(d => String(d.imei).trim() === imei);
      if (!device) {
        skippedCount++;
        errors.push(`Device IMEI "${imei}" not found`);
        return;
      }

      // Find SIM by SIM No
      const simNo = (row.simNo || '').toString().trim();
      const sim = sims.find(s => String(s.simNo).trim() === simNo);
      if (!sim) {
        skippedCount++;
        errors.push(`SIM "${simNo}" not found`);
        return;
      }

      // Find company
      let companyId = null;
      if (isSuperAdmin && row.companyName) {
        const company = companies.find(c => c.name.toLowerCase() === row.companyName.toString().toLowerCase());
        if (company) {
          companyId = company.id;
          if (row.subCompanyName) {
            const subCompany = subCompanies.find(
              s => String(s.parentCompanyId) === String(company.id) && 
              s.name.toLowerCase() === row.subCompanyName.toString().toLowerCase()
            );
            if (subCompany) companyId = subCompany.id;
          }
        } else {
          skippedCount++;
          errors.push(`Company "${row.companyName}" not found`);
          return;
        }
      } else if (isInstaller) {
        companyId = userSubCompanyId || userCompanyId;
      } else if (row.companyName) {
        const company = companies.find(c => c.name.toLowerCase() === row.companyName.toString().toLowerCase());
        if (company) {
          companyId = company.id;
          if (row.subCompanyName) {
            const subCompany = subCompanies.find(
              s => String(s.parentCompanyId) === String(company.id) && 
              s.name.toLowerCase() === row.subCompanyName.toString().toLowerCase()
            );
            if (subCompany) companyId = subCompany.id;
          }
        } else {
          skippedCount++;
          errors.push(`Company "${row.companyName}" not found`);
          return;
        }
      }

      // If still no company, use the current user's company
      if (!companyId) {
        companyId = userCompanyId || userSubCompanyId;
      }

      if (!companyId) {
        skippedCount++;
        errors.push(`No company found. Please select a company.`);
        return;
      }

      // Find installer
      let installerName = '';
      let installerId = null;
      if (row.installerUsername) {
        const installer = installers.find(
          i => i.username.toLowerCase() === row.installerUsername.toString().toLowerCase()
        );
        if (installer) {
          installerName = installer.username;
          installerId = installer.id;
        }
      }

      if (isInstaller) {
        installerName = currentUser.username;
        installerId = currentUser.id;
      }

      // Validate vehicle type
      const vehicleType = (row.vehicleType || 'bike').toString().toLowerCase().trim();
      if (!['bike', 'bus', 'truck', 'hatchback', 'sedan', 'suv', 'van', 'car'].includes(vehicleType)) {
        skippedCount++;
        errors.push(`Invalid vehicle type "${vehicleType}"`);
        return;
      }

      // Create vehicle data
      const vehicleData = {
        licensePlate: plate,
        company: companyId,
        imei: device.id,
        sim: sim.id,
        vehicleType: vehicleType,
        installed_by: installerName || '',
        installer_id: installerId,
        panic_buttons: 0,
        accessories: [],
        images: [],
        category: '',
        status: 'active'
      };

      await addVehicle(vehicleData);
      await updateDevice(device.id, { ...device, status: 'in use' });
      await updateSim(sim.id, { ...sim, status: 'active' });
      
      addedCount++;
    } catch (error) {
      skippedCount++;
      errors.push(`${error.message}`);
      console.error('Error adding vehicle:', error);
    }
  }

  // Return results for this chunk
  if (errors.length > 0) {
    throw new Error(errors.join('; '));
  }

  return { addedCount, skippedCount, errors };
};

// FIXED: Bulk update handler with proper chunk handling
const handleBulkUpdate = (rows) => {
  console.log(`📥 Processing chunk of ${rows.length} vehicle updates`);
  
  let updatedCount = 0;
  let skippedCount = 0;
  const errors = [];

  rows.forEach((row) => {
    try {
      const plate = (row.licensePlate || '').toString().trim();

      if (!plate) {
        // If no license plate, try to release device/SIM
        const imei = (row.imei || '').toString().trim();
        const simNo = (row.simNo || '').toString().trim();

        if (imei && simNo) {
          const device = devices.find(d => String(d.imei).trim() === imei);
          const sim = sims.find(s => String(s.simNo).trim() === simNo);

          if (device) {
            const vehicleWithDevice = vehicles.find(v => String(v.imei) === String(device.id));
            if (vehicleWithDevice) {
              updateVehicle(vehicleWithDevice.id, { ...vehicleWithDevice, imei: null });
              updateDevice(device.id, { ...device, status: 'available' });
              updatedCount++;
            }
          }
          if (sim) {
            const vehicleWithSim = vehicles.find(v => String(v.sim) === String(sim.id));
            if (vehicleWithSim) {
              updateVehicle(vehicleWithSim.id, { ...vehicleWithSim, sim: null });
              updateSim(sim.id, { ...sim, status: 'available' });
              updatedCount++;
            }
          }
        }
        return;
      }

      const vehicle = vehicles.find(v => v.licensePlate.toLowerCase() === plate.toLowerCase());
      if (!vehicle) {
        skippedCount++;
        errors.push(`Vehicle "${plate}" not found.`);
        return;
      }

      // Determine the company ID
      let finalCompanyId = vehicle.company;
      
      if (isSuperAdmin && row.companyName) {
        const parentCompany = companies.find(c => c.name.toLowerCase() === (row.companyName || '').toString().toLowerCase());
        if (parentCompany) {
          finalCompanyId = parentCompany.id;
          if (row.subCompanyName && row.subCompanyName.toString().trim() !== '') {
            const sub = subCompanies.find(
              s => String(s.parentCompanyId) === String(parentCompany.id) &&
                s.name.toLowerCase() === row.subCompanyName.toString().trim().toLowerCase()
            );
            if (sub) finalCompanyId = sub.id;
          }
        } else {
          skippedCount++;
          errors.push(`Company "${row.companyName}" not found for vehicle "${plate}"`);
          return;
        }
      } else if (row.companyName) {
        const company = companies.find(c => c.name.toLowerCase() === row.companyName.toString().toLowerCase());
        if (company) {
          finalCompanyId = company.id;
          if (row.subCompanyName) {
            const subCompany = subCompanies.find(
              s => String(s.parentCompanyId) === String(company.id) &&
                s.name.toLowerCase() === row.subCompanyName.toString().toLowerCase()
            );
            if (subCompany) finalCompanyId = subCompany.id;
          }
        } else {
          skippedCount++;
          errors.push(`Company "${row.companyName}" not found for vehicle "${plate}"`);
          return;
        }
      }

      let installerUpdate = {};
      if (row.installerUsername && row.installerUsername.toString().trim() !== '') {
        const installer = installers.find(inst => inst.username === row.installerUsername.toString().trim());
        if (installer) {
          installerUpdate = {
            installed_by: installer.username,
            installer_id: installer.id,
          };
        } else {
          skippedCount++;
          errors.push(`Installer "${row.installerUsername}" not found for vehicle "${plate}"`);
          return;
        }
      }

      // Update vehicle
      const updateData = {
        ...vehicle,
        company: finalCompanyId,
        ...installerUpdate,
      };

      if (row.vehicleType) {
        const vehicleType = row.vehicleType.toString().trim();
        if (['bike', 'bus', 'truck', 'hatchback', 'sedan', 'suv', 'van','car'].includes(vehicleType)) {
          updateData.vehicleType = vehicleType;
        } else {
          skippedCount++;
          errors.push(`Invalid vehicle type "${vehicleType}" for vehicle "${plate}"`);
          return;
        }
      }

      updateVehicle(vehicle.id, updateData);
      updatedCount++;
    } catch (error) {
      skippedCount++;
      errors.push(`${error.message}`);
      console.error('Error updating vehicle:', error);
    }
  });

  // Return results for this chunk
  return { updatedCount, skippedCount, errors };
};

const handleAccessoryCheckboxChange = (option) => {
    setForm(prev => {
      const current = prev.accessories || [];
      const updated = current.includes(option)
        ? current.filter(item => item !== option)
        : [...current, option];
      return { ...prev, accessories: updated };
    });
  };

  const handleAddCustomAccessory = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const cleanValue = customAccessoryInput.trim();
    if (!cleanValue) return;

    if (!accessoryOptions.some(opt => opt.toLowerCase() === cleanValue.toLowerCase())) {
      const updatedOptions = [...accessoryOptions, cleanValue];
      setAccessoryOptions(updatedOptions);
      localStorage.setItem('vehicleAccessories', JSON.stringify(updatedOptions));
    }
    setForm(prev => {
      const current = prev.accessories || [];
      if (!current.includes(cleanValue)) {
        return { ...prev, accessories: [...current, cleanValue] };
      }
      return prev;
    });
    setCustomAccessoryInput('');
  };

  const handlePreviewImage = (images) => {
    setPreviewImages(images);
    setCurrentImageIndex(0);
  };

  const handlePrevImage = useCallback(() => {
    setCurrentImageIndex(prev => 
      prev === 0 ? previewImages.length - 1 : prev - 1
    );
  }, [previewImages]);

  const handleNextImage = useCallback(() => {
    setCurrentImageIndex(prev => 
      prev === previewImages.length - 1 ? 0 : prev + 1
    );
  }, [previewImages]);

  const handleClosePreview = useCallback(() => {
    setPreviewImages(null);
    setCurrentImageIndex(0);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!previewImages) return;
      if (e.key === 'ArrowLeft') handlePrevImage();
      if (e.key === 'ArrowRight') handleNextImage();
      if (e.key === 'Escape') handleClosePreview();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [previewImages, handlePrevImage, handleNextImage, handleClosePreview]);

  const formatLastUpdate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${day}-${month}-${year} ${hours}:${minutes}`;
    } catch (e) {
      return '-';
    }
  };

  return (
    <div className="vehicle-details-container">
      {notification.visible && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-xl shadow-lg transition-all duration-300 ${
          notification.type === 'error' ? 'bg-red-500' : 'bg-green-500'
        } text-white font-medium`}>
          {notification.message}
        </div>
      )}

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold text-gray-800">Vehicle Details</h1>
        <div className="flex items-center space-x-3 flex-wrap gap-2">
          {(isSuperAdmin || isInstaller || isTeamLead) && (
            <>
              <button onClick={() => { resetForm(); setShowForm(true); }} className="btn-primary">
                <FaPlus /> Add Vehicle
              </button>

              <div className="relative inline-block text-left" ref={addMenuRef}>
                <button 
                  onClick={() => { setIsAddMenuOpen(!isAddMenuOpen); setIsUpdateMenuOpen(false); }} 
                  className="btn-primary flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-medium transition"
                >
                  <span>Bulk Upload</span>
                  <FaChevronDown className="text-xs" />
                </button>
                
                {isAddMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 rounded-xl bg-white shadow-xl ring-1 ring-black ring-opacity-5 z-50 p-2 space-y-1 border border-gray-100">
                    <BulkUpload
                      title="Vehicle"
                      downloadLabel="Download Add Template"
                      uploadLabel="Bulk Add"
                      downloadData={vehicles.map(v => ({
                        licensePlate: v.licensePlate || '',
                        companyName: getEntityDisplayName(v.company) || '',
                        subCompanyName: '',
                        imei: getDeviceImei(v.imei) || '',
                        simNo: getSimNo(v.sim) || '',
                        vehicleType: v.vehicleType || '',
                        installerUsername: v.installed_by || ''
                      }))}
                      columns={[
                        { key: 'licensePlate', label: 'License Plate' },
                        { key: 'companyName', label: 'Company Name' },
                        { key: 'subCompanyName', label: 'Sub Company Name' },
                        { key: 'imei', label: 'IMEI' },
                        { key: 'simNo', label: 'SIM No' },
                        { key: 'vehicleType', label: 'Vehicle Type' },
                        { key: 'installerUsername', label: 'Installer Username' }
                      ]}
                      sampleRow={{
                        licensePlate: 'KA01AB1234',
                        companyName: 'ABC Company',
                        subCompanyName: 'Branch 1',
                        imei: '123456789012345',
                        simNo: '9876543210',
                        vehicleType: 'bike',
                        installerUsername: 'installer1'
                      }}
                      onUpload={handleBulkAdd}
                      validateRow={validateRow}
                    />
                  </div>
                )}
              </div>

              <div className="relative inline-block text-left" ref={updateMenuRef}>
                <button 
                  onClick={() => { setIsUpdateMenuOpen(!isUpdateMenuOpen); setIsAddMenuOpen(false); }} 
                  className="btn-primary flex items-center gap-2 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-xl text-sm font-medium transition"
                >
                  <span>Bulk Update</span>
                  <FaChevronDown className="text-xs" />
                </button>

                {isUpdateMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 rounded-xl bg-white shadow-xl ring-1 ring-black ring-opacity-5 z-50 p-2 space-y-1 border border-gray-100">
                    <BulkUpload
                      title="Vehicle Bulk Update"
                      downloadLabel="Download Template"
                      uploadLabel="Bulk Update"
                      downloadData={vehicles.map(v => ({
                        licensePlate: v.licensePlate || '',
                        companyName: getEntityDisplayName(v.company) || '',
                        subCompanyName: '',
                        vehicleType: v.vehicleType || '',
                        installerUsername: v.installed_by || '',
                        imei: getDeviceImei(v.imei) || '',
                        simNo: getSimNo(v.sim) || ''
                      }))}
                      columns={[
                        { key: 'licensePlate', label: 'License Plate (empty to release)' },
                        { key: 'companyName', label: 'Company Name' },
                        { key: 'subCompanyName', label: 'Sub‑Company Name (optional)' },
                        { key: 'vehicleType', label: 'Vehicle Type (optional)' },
                        { key: 'installerUsername', label: 'Installer Username (optional)' },
                        { key: 'imei', label: 'IMEI (only if releasing)' },
                        { key: 'simNo', label: 'SIM No (only if releasing)' },
                      ]}
                      sampleRow={{
                        licensePlate: 'KA01AB1234',
                        companyName: 'Example Co',
                        subCompanyName: 'Sub1',
                        vehicleType: 'sedan',
                        installerUsername: '',
                        imei: '',
                        simNo: '',
                      }}
                      onUpload={handleBulkUpdate}
                      validateRow={() => []}
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* ✅ RESPONSIVE Filters, Search & Download */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full lg:w-auto">
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Type:</label>
            <select
              value={vehicleTypeFilter}
              onChange={(e) => { setVehicleTypeFilter(e.target.value); setCurrentPage(1); }}
              className="flex-1 sm:flex-none rounded-xl border border-gray-300 px-3 py-2 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 min-w-[110px]"
            >
              <option value="">All</option>
              <option value="bike">Bike</option>
              <option value="bus">Bus</option>
              <option value="truck">Truck</option>
              <option value="hatchback">Hatchback</option>
              <option value="sedan">Sedan</option>
              <option value="suv">SUV</option>
              <option value="van">Van</option>
              <option value="car">Car</option>
            </select>
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
              className="flex-1 sm:flex-none rounded-xl border border-gray-300 px-3 py-2 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 min-w-[110px]"
            >
              <option value="">All</option>
              <option value="ignition">On</option>
              <option value="off">Off</option>
              <option value="sos">SOS</option>
            </select>
          </div>

          {/* ✅ NEW: Installed By Filter Dropdown */}
          <div className="relative" ref={installedByFilterRef}>
            <button
              type="button"
              onClick={() => setIsInstalledByFilterOpen(!isInstalledByFilterOpen)}
              className="flex items-center justify-between rounded-xl border border-gray-300 px-3 py-2 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 min-w-[140px] w-full sm:w-auto"
            >
              <span className="text-gray-700 flex-1 text-left">
                {installedByFilter ? installedByFilter : 'Installed By'}
              </span>
              <FaChevronDown className="text-gray-400 text-xs ml-2" />
            </button>

            {isInstalledByFilterOpen && (
              <div className="absolute left-0 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-xl z-50 p-2 max-h-60 flex flex-col min-w-[200px]">
                <div className="flex space-x-1.5 mb-2 sticky top-0 bg-white pb-1.5 border-b border-gray-100">
                  <input
                    type="text"
                    placeholder="Search installer..."
                    className="flex-1 rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    value={installedBySearch}
                    onChange={(e) => setInstalledBySearch(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setInstalledByFilter('');
                      setInstalledBySearch('');
                    }}
                    className="px-2 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-600 rounded-lg text-xs font-medium transition"
                  >
                    Clear
                  </button>
                </div>

                <div className="overflow-y-auto flex-1 space-y-1 max-h-40">
                  <div
                    onClick={() => {
                      setInstalledByFilter('');
                      setIsInstalledByFilterOpen(false);
                    }}
                    className="px-3 py-2 text-xs text-gray-500 rounded-lg hover:bg-gray-100 cursor-pointer"
                  >
                    All Installers
                  </div>
                  {filteredInstalledBy.map((installer) => (
                    <div
                      key={installer}
                      onClick={() => {
                        setInstalledByFilter(installer);
                        setIsInstalledByFilterOpen(false);
                      }}
                      className={`px-3 py-2 text-xs rounded-lg cursor-pointer transition ${
                        installedByFilter === installer
                          ? 'bg-indigo-50 text-indigo-700 font-semibold'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {installer}
                    </div>
                  ))}
                  {filteredInstalledBy.length === 0 && (
                    <div className="px-3 py-2 text-xs text-gray-400 text-center">No installers found</div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ✅ NEW: Accessory Filter Dropdown */}
          <div className="relative" ref={accessoryFilterRef}>
            <button
              type="button"
              onClick={() => setIsAccessoryFilterOpen(!isAccessoryFilterOpen)}
              className="flex items-center justify-between rounded-xl border border-gray-300 px-3 py-2 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 min-w-[140px] w-full sm:w-auto"
            >
              <span className="text-gray-700 flex-1 text-left">
                {selectedAccessories.length > 0 
                  ? `${selectedAccessories.length} Selected` 
                  : 'Accessories'}
              </span>
              <FaChevronDown className="text-gray-400 text-xs ml-2" />
            </button>

            {isAccessoryFilterOpen && (
              <div className="absolute left-0 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-xl z-50 p-2 max-h-60 flex flex-col min-w-[220px]">
                <div className="flex space-x-1.5 mb-2 sticky top-0 bg-white pb-1.5 border-b border-gray-100">
                  <input
                    type="text"
                    placeholder="Search accessories..."
                    className="flex-1 rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    value={accessoryFilterSearch}
                    onChange={(e) => setAccessoryFilterSearch(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <button
                    type="button"
                    onClick={clearAccessoryFilters}
                    className="px-2 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-600 rounded-lg text-xs font-medium transition"
                  >
                    Clear
                  </button>
                </div>

                <div className="overflow-y-auto flex-1 space-y-1 max-h-40">
                  {(filteredAccessoryOptions.length > 0 ? filteredAccessoryOptions : allVehicleAccessories).map((accessory) => {
                    const isChecked = selectedAccessories.includes(accessory);
                    return (
                      <label 
                        key={accessory} 
                        className="flex items-center space-x-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 cursor-pointer select-none text-xs"
                      >
                        <input 
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleAccessoryFilter(accessory)}
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 h-3.5 w-3.5"
                        />
                        <span className="text-gray-700 font-medium">{accessory}</span>
                      </label>
                    );
                  })}
                  {filteredAccessoryOptions.length === 0 && allVehicleAccessories.length === 0 && (
                    <div className="px-3 py-2 text-xs text-gray-400 text-center">No accessories found</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-2 flex-1">
          <input
            type="text"
            placeholder="Search by license plate, IMEI, Mobile No, SIM no…"
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
            className="w-full sm:flex-1 rounded-xl border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          
          <button 
            onClick={handleDownloadExcel}
            className="btn-secondary flex items-center gap-2 hover:bg-gray-100 whitespace-nowrap w-full sm:w-auto justify-center"
          >
            <FaDownload className="text-sm" /> Download
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-100">
        <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">License Plate</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Company / Sub‑Company</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">IMEI</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Mobile No</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">SIM No</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Vehicle Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Category</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Panic Buttons</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Accessories</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Images</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">GPS Last Update</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Ignition / SOS</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Lat / Long</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">FCR</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                {(isSuperAdmin || isInstaller || isTeamLead) && <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>}
                {(isSuperAdmin || isAdmin) && <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Installed By</th>}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {paginatedVehicles.map(v => {
                const ignitionOn = v.ignition === true;
                const sosOn = v.sos === true;
                const hasFCR = v.fcrData !== undefined && v.fcrData !== null;
                const mobileNo = getMobileNo(v.sim);
                const simNo = getSimNo(v.sim);
                const lastUpdate = v.last_update || v.updatedAt || v.createdAt || null;
                
                return (
                  <tr key={v.id} className="hover:bg-indigo-50/50 transition-colors">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{v.licensePlate}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{getEntityDisplayName(v.company)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{getDeviceImei(v.imei)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{mobileNo || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{simNo || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 capitalize">{v.vehicleType}</td>
                    
                    <td className="px-4 py-3 text-sm">
                      {v.category ? (
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                          v.category === 'New installation' ? 'bg-green-100 text-green-800' :
                          v.category === 'Re installation' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {v.category}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs italic">Not set</span>
                      )}
                    </td>

                    <td className="px-4 py-3 text-center text-sm font-semibold text-indigo-600">
                      {v.panic_buttons !== undefined && v.panic_buttons !== null 
                        ? String(v.panic_buttons) 
                        : (v.panicButtons !== undefined && v.panicButtons !== null ? String(v.panicButtons) : '0')}
                    </td>

                    <td className="px-4 py-3 text-sm text-gray-600">
                      {v.accessories && v.accessories.length > 0 ? v.accessories.join(', ') : '-'}
                    </td>

                    <td className="px-4 py-3 text-center text-sm">
                      {v.images && v.images.length > 0 ? (
                        <button
                          type="button"
                          onClick={() => handlePreviewImage(v.images)}
                          className="text-indigo-600 hover:text-indigo-900 p-1 transition flex items-center justify-center mx-auto"
                          title="View Uploaded Images"
                        >
                          <FaEye size={16} />
                        </button>
                      ) : (
                        <span className="text-gray-400 text-xs italic">No images uploaded</span>
                      )}
                    </td>

                    <td className="px-4 py-3 text-center text-xs font-mono text-gray-600 whitespace-nowrap">
                      {v.gps_time ? formatLastUpdate(v.gps_time) : '-'}
                    </td>

                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <svg 
                          width="20" 
                          height="20" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke={ignitionOn ? "#22C55E" : "#9CA3AF"} 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                          className={ignitionOn ? "animate-pulse" : ""}
                        >
                          <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.78 7.78 5.5 5.5 0 0 1 7.78-7.78zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
                        </svg>

                        <div className="relative flex items-center justify-center">
                          <svg 
                            width="20" 
                            height="20" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke={sosOn ? "#EF4444" : "#9CA3AF"} 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                            className={sosOn ? "animate-bell-ring" : ""}
                          >
                            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                          </svg>
                          
                          {sosOn && (
                            <>
                              <span className="absolute -top-1 -right-1 flex h-3 w-3">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                              </span>
                              <span className="absolute inset-0 animate-pulse-ring rounded-full bg-red-500/20"></span>
                            </>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-xs font-mono text-gray-600">
                      {v.latitude && v.longitude
                        ? `${Number(v.latitude).toFixed(6)}, ${Number(v.longitude).toFixed(6)}`
                        : "-"}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {(v.fcrData || v.fcr_data) ? (
                        <button
                          type="button"
                          onClick={() => handleViewReport(v)}
                          className="text-green-600 hover:text-green-800 p-1.5 transition flex items-center justify-center mx-auto"
                          title="View FCR Report"
                        >
                          <FaFileAlt size={18} />
                        </button>
                      ) : (
                        <span className="text-gray-400 text-xs italic">No FCR</span>
                      )}
                    </td>

                    <td className="px-4 py-3 text-sm">
                      {v.status ? (
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                          v.status === 'active' ? 'bg-green-100 text-green-800' :
                          v.status === 'inactive' ? 'bg-gray-100 text-gray-800' :
                          v.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {v.status}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs italic">-</span>
                      )}
                    </td>

                    {(isSuperAdmin || isInstaller || isTeamLead) && (
                      <td className="px-4 py-3 text-sm">
                        <div className="flex space-x-2">
                          <button onClick={() => handleEdit(v)} className="text-indigo-600 hover:text-indigo-900 transition">
                            <FaEdit />
                          </button>
                          {isSuperAdmin && (
                            <button onClick={() => handleDelete(v.id)} className="text-red-500 hover:text-red-700 transition">
                              <FaTrash />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                    {(isSuperAdmin || isAdmin) && (
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {v.installed_by || '-'}
                      </td>
                    )}
                  </tr>
                );
              })}
              {paginatedVehicles.length === 0 && (
                <tr>
                  <td colSpan={(isSuperAdmin || isInstaller || isTeamLead) ? "17" : "15"} className="px-4 py-8 text-center text-sm text-gray-500">
                    No vehicles added.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination
        totalItems={totalItems}
        pageSize={pageSize}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
      />

      {showForm && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto">
          <div className="bg-white p-4 sm:p-6 rounded-2xl shadow-2xl w-full max-w-lg mx-auto my-4 sm:my-8 max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-4">{editingId ? 'Edit Vehicle' : 'Add Vehicle'}</h2>
            <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">License Plate No *</label>
                <input
                  className="w-full rounded-xl border border-gray-300 px-3 sm:px-4 py-2 sm:py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  value={form.licensePlate}
                  onChange={e => setForm({ ...form, licensePlate: e.target.value })}
                  required
                />
                {errors.licensePlate && <p className="text-red-500 text-xs mt-1">{errors.licensePlate}</p>}
              </div>

              {!isInstaller && (
                <>
                  {isSuperAdmin ? (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                        <select
                          className="w-full rounded-xl border border-gray-300 px-3 sm:px-4 py-2 sm:py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                          value={selectedParentId}
                          onChange={e => {
                            const parentId = String(e.target.value);
                            setSelectedParentId(parentId);
                            setSelectedSubId('');
                            setForm(prev => ({ ...prev, company: parentId }));
                          }}
                          required
                        >
                          <option value="">Select Company</option>
                          {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                      </div>
                      {subCompanyOptions.length > 0 && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Sub‑Company (optional)</label>
                          <select
                            className="w-full rounded-xl border border-gray-300 px-3 sm:px-4 py-2 sm:py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                            value={selectedSubId}
                            onChange={e => {
                              const subId = String(e.target.value);
                              setSelectedSubId(subId);
                              if (subId) {
                                setForm({ ...form, company: subId });
                              } else {
                                setForm({ ...form, company: selectedParentId });
                              }
                            }}
                          >
                            <option value="">-- Use main company --</option>
                            {subCompanyOptions.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                          </select>
                        </div>
                      )}
                    </>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                      <select
                        className="w-full rounded-xl border border-gray-300 px-3 sm:px-4 py-2 sm:py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                        value={form.company}
                        onChange={e => setForm({ ...form, company: e.target.value })}
                        required
                      >
                        <option value="">Select Company</option>
                        {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                  )}
                </>
              )}

              <div className="relative" ref={imeiRef}>
                <label className="block text-sm font-medium text-gray-700 mb-1">IMEI (Device) *</label>
                
                <button
                  type="button"
                  onClick={() => setIsImeiOpen(!isImeiOpen)}
                  className="w-full flex items-center justify-between rounded-xl border border-gray-300 px-3 sm:px-4 py-2 sm:py-2.5 bg-white text-sm text-left focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <span className={selectedDeviceObj ? 'text-gray-900' : 'text-gray-400'}>
                    {selectedDeviceObj ? `${selectedDeviceObj.imei} (${selectedDeviceObj.status})` : 'Select IMEI'}
                  </span>
                  <FaChevronDown className="text-gray-400 text-xs" />
                </button>

                {isImeiOpen && (
                  <div className="absolute left-0 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-xl z-50 p-2 max-h-60 flex flex-col">
                    <div className="flex space-x-1.5 mb-2 sticky top-0 bg-white pb-1.5 border-b border-gray-100">
                      <input
                        type="text"
                        placeholder="Search digits..."
                        className="flex-1 rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        value={imeiSearchInput}
                        onChange={(e) => setImeiSearchInput(e.target.value)}
                        onClick={(e) => e.stopPropagation()} 
                      />
                      <button
                        type="button"
                        onClick={handleImeiSearch}
                        className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-medium hover:bg-indigo-700 transition flex items-center space-x-1"
                      >
                        <FaSearch className="text-[10px]" />
                        <span>Search</span>
                      </button>
                    </div>

                    <div className="overflow-y-auto flex-1 space-y-0.5 max-h-40">
                      <div
                        onClick={() => { setForm({ ...form, imei: '' }); setIsImeiOpen(false); }}
                        className="px-3 py-2 text-xs text-gray-500 rounded-lg hover:bg-gray-100 cursor-pointer"
                      >
                        Select IMEI
                      </div>
                      {searchedDevices.map((device) => (
                        <div
                          key={device.id}
                          onClick={() => {
                            setForm({ ...form, imei: String(device.id) });
                            setIsImeiOpen(false);
                          }}
                          className={`px-3 py-2 text-xs rounded-lg cursor-pointer transition ${
                            String(form.imei) === String(device.id)
                              ? 'bg-indigo-50 text-indigo-700 font-semibold'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {device.imei} ({device.status})
                        </div>
                      ))}
                      {searchedDevices.length === 0 && (
                        <div className="px-3 py-2 text-xs text-gray-400 text-center">No matches found</div>
                      )}
                    </div>
                  </div>
                )}
                {errors.imei && <p className="text-red-500 text-xs mt-1">{errors.imei}</p>}
              </div>

              <div className="relative" ref={simRef}>
                <label className="block text-sm font-medium text-gray-700 mb-1">SIM *</label>
                
                <button
                  type="button"
                  onClick={() => setIsSimOpen(!isSimOpen)}
                  className="w-full flex items-center justify-between rounded-xl border border-gray-300 px-3 sm:px-4 py-2 sm:py-2.5 bg-white text-sm text-left focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <span className={selectedSimObj ? 'text-gray-900' : 'text-gray-400'}>
                    {selectedSimObj ? `${selectedSimObj.simNo} - ${selectedSimObj.mobileNo}` : 'Select SIM'}
                  </span>
                  <FaChevronDown className="text-gray-400 text-xs" />
                </button>

                {isSimOpen && (
                  <div className="absolute left-0 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-xl z-50 p-2 max-h-60 flex flex-col">
                    <div className="flex space-x-1.5 mb-2 sticky top-0 bg-white pb-1.5 border-b border-gray-100">
                      <input
                        type="text"
                        placeholder="Search number..."
                        className="flex-1 rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        value={simSearchInput}
                        onChange={(e) => setSimSearchInput(e.target.value)}
                        onClick={(e) => e.stopPropagation()} 
                      />
                      <button
                        type="button"
                        onClick={handleSimSearch}
                        className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-medium hover:bg-indigo-700 transition flex items-center space-x-1"
                      >
                        <FaSearch className="text-[10px]" />
                        <span>Search</span>
                      </button>
                    </div>

                    <div className="overflow-y-auto flex-1 space-y-0.5 max-h-40">
                      <div
                        onClick={() => { setForm({ ...form, sim: '' }); setIsSimOpen(false); }}
                        className="px-3 py-2 text-xs text-gray-500 rounded-lg hover:bg-gray-100 cursor-pointer"
                      >
                        Select SIM
                      </div>
                      {searchedSims.map((sim) => (
                        <div
                          key={sim.id}
                          onClick={() => {
                            setForm({ ...form, sim: String(sim.id) });
                            setIsSimOpen(false);
                          }}
                          className={`px-3 py-2 text-xs rounded-lg cursor-pointer transition ${
                            String(form.sim) === String(sim.id)
                              ? 'bg-indigo-50 text-indigo-700 font-semibold'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {sim.simNo} - {sim.mobileNo} ({sim.status})
                        </div>
                      ))}
                      {searchedSims.length === 0 && (
                        <div className="px-3 py-2 text-xs text-gray-400 text-center">No matches found</div>
                      )}
                    </div>
                  </div>
                )}
                {errors.sim && <p className="text-red-500 text-xs mt-1">{errors.sim}</p>}
              </div>

              {form.sim && !editingId && (
                <div className="border border-gray-200 rounded-xl p-3 bg-gray-50">
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="text-sm font-medium text-gray-700">SIM Status:</label>
                    
                    <button
                      type="button"
                      onClick={checkSimStatus}
                      disabled={isSimStatusLoading}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg text-sm font-medium transition flex items-center gap-2"
                    >
                      {isSimStatusLoading ? (
                        <>
                          <span className="animate-spin">⟳</span> Checking...
                        </>
                      ) : (
                        <>
                          <FaWifi size={14} /> Status
                        </>
                      )}
                    </button>

                    {showSimStatus && simStatus && (
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        simStatus === 'active' 
                          ? 'bg-green-100 text-green-800 border border-green-300' 
                          : simStatus === 'available'
                          ? 'bg-yellow-100 text-yellow-800 border border-yellow-300'
                          : 'bg-red-100 text-red-800 border border-red-300'
                      }`}>
                        {simStatus.toUpperCase()}
                      </span>
                    )}

                    {showSimStatus && simStatus === 'available' && (
                      <button
                        type="button"
                        onClick={connectSim}
                        disabled={isSimStatusLoading}
                        className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg text-sm font-medium transition flex items-center gap-2"
                      >
                        {isSimStatusLoading ? (
                          <>
                            <span className="animate-spin">⟳</span> Connecting...
                          </>
                        ) : (
                          <>
                            <FaSync size={14} /> Connect
                          </>
                        )}
                      </button>
                    )}

                    {showSimStatus && simStatus === 'active' && (
                      <span className="text-green-600 text-xs font-medium ml-1">
                        ✅ SIM is active - ready for installation
                      </span>
                    )}
                  </div>
                </div>
              )}

              {!editingId && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    className="w-full rounded-xl border border-gray-300 px-3 sm:px-4 py-2 sm:py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                    value={form.category}
                    onChange={e => setForm({ ...form, category: e.target.value })}
                  >
                    <option value="">Select Category</option>
                    {categoryOptions.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Panic Buttons</label>
                  <select
                    className="w-full rounded-xl border border-gray-300 px-3 sm:px-4 py-2 sm:py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                    value={form.panicButtons}
                    onChange={e => setForm({ ...form, panicButtons: Number(e.target.value) })}
                  >
                    <option value={0}>No Panic Buttons</option>
                    {Array.from({ length: 13 }, (_, index) => {
                      const numberValue = index + 1;
                      return (
                        <option key={numberValue} value={numberValue}>
                          {numberValue}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div className="relative" ref={accessoryRef}>
                  <div className="flex justify-between items-center mb-1">
                    <label className="block text-sm font-medium text-gray-700">Accessories</label>
                    {isSuperAdmin && (
                      <div className="flex items-center space-x-1">
                        <input 
                          type="text"
                          placeholder="Add custom..."
                          value={customAccessoryInput}
                          onChange={(e) => setCustomAccessoryInput(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className="rounded-lg border border-gray-300 px-2 py-1 text-[11px] focus:outline-none focus:ring-1 focus:ring-indigo-500 max-w-[90px]"
                        />
                        <button 
                          type="button"
                          onClick={handleAddCustomAccessory}
                          className="bg-green-600 hover:bg-green-700 text-white p-1 rounded-lg transition text-xs flex items-center justify-center"
                          title="Add Custom Option"
                        >
                          <FaPlus size={10} />
                        </button>
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => setIsAccessoryDropdownOpen(!isAccessoryDropdownOpen)}
                    className="w-full flex items-center justify-between rounded-xl border border-gray-300 px-3 sm:px-4 py-2 sm:py-2.5 bg-white text-sm text-left focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <span className={(form.accessories && form.accessories.length > 0) ? 'text-gray-900 truncate' : 'text-gray-400'}>
                      {form.accessories && form.accessories.length > 0 ? form.accessories.join(', ') : 'None Selected'}
                    </span>
                    <FaChevronDown className="text-gray-400 text-xs" />
                  </button>

                  {isAccessoryDropdownOpen && (
                    <div className="absolute left-0 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-xl z-50 p-2 max-h-48 overflow-y-auto space-y-1">
                      {accessoryOptions.map((option) => {
                        const isChecked = form.accessories?.includes(option);
                        return (
                          <label 
                            key={option} 
                            className="flex items-center space-x-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 cursor-pointer select-none text-xs"
                          >
                            <input 
                              type="checkbox"
                              checked={!!isChecked}
                              onChange={() => handleAccessoryCheckboxChange(option)}
                              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 h-3.5 w-3.5"
                            />
                            <span className="text-gray-700 font-medium">{option}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Type</label>
                <select
                  className="w-full rounded-xl border border-gray-300 px-3 sm:px-4 py-2 sm:py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                  value={form.vehicleType}
                  onChange={e => setForm({ ...form, vehicleType: e.target.value })}
                >
                  <option value="bike">Bike</option>
                  <option value="bus">Bus</option>
                  <option value="truck">Truck</option>
                  <option value="hatchback">Hatchback</option>
                  <option value="sedan">Sedan</option>
                  <option value="suv">SUV</option>
                  <option value="van">Van</option>
                  <option value="car">Car</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Upload Vehicle Images</label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="file"
                    multiple
                    accept="image/jpeg, image/png, image/jpg"
                    onChange={handleImageChange}
                    className="flex-1 text-xs sm:text-sm text-gray-500 file:mr-4 file:py-2 file:px-3 sm:file:px-4 file:rounded-xl file:border-0 file:text-xs sm:file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 cursor-pointer"
                  />
                  <button
                    type="button"
                    onClick={() => cameraInputRef.current?.click()}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-xl transition flex items-center justify-center gap-2 text-sm whitespace-nowrap"
                  >
                    <FaCamera /> Camera
                  </button>
                  <input
                    ref={cameraInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleCameraCapture}
                    className="hidden"
                  />
                </div>
                {form.images && form.images.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2 max-h-24 overflow-y-auto border border-gray-100 p-1.5 rounded-xl">
                    {form.images.map((img, index) => (
                      <div key={index} className="relative w-12 h-12 rounded-lg overflow-hidden border border-gray-200">
                        <img src={img} alt="preview" className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => removeSelectedFormImage(index)}
                          className="absolute top-0 right-0 bg-red-500 text-white p-0.5 rounded-bl hover:bg-red-600 transition"
                        >
                          <FaTimes size={8} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {!isInstaller && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Installer (optional)</label>
                  <select
                    className="w-full rounded-xl border border-gray-300 px-3 sm:px-4 py-2 sm:py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                    value={form.installerUsername}
                    onChange={e => setForm({ ...form, installerUsername: e.target.value })}
                  >
                    <option value="">-- No installer --</option>
                    {allowedInstallerIds.map(inst => (
                      <option key={inst.id} value={inst.username}>
                        {inst.username} {inst.phone ? `- ${inst.phone}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button type="button" onClick={resetForm} className="btn-secondary">Cancel</button>
                <button type="submit" className="btn-primary">{editingId ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showReportForm && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[9999] p-2 sm:p-4 overflow-y-auto">
          <div className="bg-white p-4 sm:p-6 rounded-2xl shadow-2xl w-full max-w-4xl mx-auto my-4 sm:my-8 max-h-[95vh] sm:max-h-[90vh] overflow-y-auto relative z-[10000]">

            <form onSubmit={handleReportSubmit} id="fcr-report-form">
              <div className="flex flex-wrap justify-between items-start gap-2 mb-4">
                <button 
                  type="button" 
                  onClick={handleGoBack} 
                  className="btn-secondary flex items-center gap-2 text-sm"
                >
                  <FaArrowLeft /> Back
                </button>
                <div className="flex flex-wrap gap-2">
                  <button 
                    type="button" 
                    onClick={handleReportSkip} 
                    className={`btn-secondary text-sm ${isInstaller ? 'opacity-50 cursor-not-allowed' : ''}`}
                    disabled={isInstaller}
                    title={isInstaller ? 'Installers cannot skip the report' : ''}
                  >
                    Skip & Save Vehicle
                  </button>
                  <button type="submit" className="btn-primary text-sm">
                    Save Vehicle & Report
                  </button>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6 border-b pb-4">
                <img 
                  src="https://cccs.cordonnx.com/Loginlogo.png" 
                  alt="Cordon Logo" 
                  className="h-12 sm:h-16 w-auto object-contain flex-shrink-0"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <div className="text-center">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Cordon Telematics Pvt. Ltd.</h2>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-700">Installation report / Field Call Report</h3>
                  <div className="text-sm text-gray-500">
                    No. {reportData.reportNumber || 'Generating...'}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Site <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className={`w-full rounded-xl border ${reportErrors.site ? 'border-red-500' : 'border-gray-300'} px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-gray-50`}
                    value={reportData.site}
                    onChange={e => {
                      setReportData(prev => ({ ...prev, site: e.target.value }));
                      if (reportErrors.site) {
                        setReportErrors(prev => ({ ...prev, site: '' }));
                      }
                    }}
                    placeholder="Company name (auto-filled)"
                    readOnly
                  />
                  {reportErrors.site && (
                    <p className="text-red-500 text-xs mt-1">{reportErrors.site}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Branch <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className={`w-full rounded-xl border ${reportErrors.branch ? 'border-red-500' : 'border-gray-300'} px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-gray-50`}
                    value={reportData.branch}
                    onChange={e => {
                      setReportData(prev => ({ ...prev, branch: e.target.value }));
                      if (reportErrors.branch) {
                        setReportErrors(prev => ({ ...prev, branch: '' }));
                      }
                    }}
                    placeholder="Place name from company (auto-filled)"
                    readOnly
                  />
                  {reportErrors.branch && (
                    <p className="text-red-500 text-xs mt-1">{reportErrors.branch}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Installation Start Date</label>
                  <input
                    type="date"
                    className="w-full rounded-xl border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    value={reportData.installationStartDate}
                    onChange={e => setReportData(prev => ({ ...prev, installationStartDate: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Installation End Date</label>
                  <input
                    type="date"
                    className="w-full rounded-xl border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    value={reportData.installationEndDate}
                    onChange={e => setReportData(prev => ({ ...prev, installationEndDate: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Billing From Date</label>
                  <input
                    type="date"
                    className="w-full rounded-xl border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-gray-50"
                    value={reportData.billingFromDate}
                    readOnly
                  />
                  <p className="text-xs text-gray-400 mt-1">Auto-set to today's date</p>
                </div>
              </div>

              <div className="mb-4">
                <h4 className="font-bold text-base bg-gray-100 p-2 rounded-t-lg border border-gray-200">BEFORE INSTALLATION</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 p-3 border border-t-0 border-gray-200 rounded-b-lg">
                  {Object.entries(reportData.beforeInstallation).map(([key, value]) => (
                    <label key={key} className="flex items-center space-x-2 text-sm">
                      <input
                        type="checkbox"
                        checked={value}
                        onChange={() => handleReportCheckboxChange('beforeInstallation', key)}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 h-4 w-4"
                      />
                      <span className="text-gray-700 text-xs sm:text-sm">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="mb-4">
                <h4 className="font-bold text-base bg-gray-100 p-2 rounded-t-lg border border-gray-200">AFTER INSTALLATION</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 p-3 border border-t-0 border-gray-200 rounded-b-lg">
                  {Object.entries(reportData.afterInstallation).map(([key, value]) => (
                    <label key={key} className="flex items-center space-x-2 text-sm">
                      <input
                        type="checkbox"
                        checked={value}
                        onChange={() => handleReportCheckboxChange('afterInstallation', key)}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 h-4 w-4"
                      />
                      <span className="text-gray-700 text-xs sm:text-sm">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="flex flex-wrap gap-4 p-3 border border-gray-200 rounded-lg">
                  <label className="flex items-center space-x-2 text-sm">
                    <input
                      type="checkbox"
                      checked={reportData.newInstallation}
                      onChange={e => setReportData(prev => ({ ...prev, newInstallation: e.target.checked }))}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 h-4 w-4"
                    />
                    <span>New Installation</span>
                  </label>
                  <label className="flex items-center space-x-2 text-sm">
                    <input
                      type="checkbox"
                      checked={reportData.reInstallation}
                      onChange={e => setReportData(prev => ({ ...prev, reInstallation: e.target.checked }))}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 h-4 w-4"
                    />
                    <span>Re-Installation</span>
                  </label>
                  <label className="flex items-center space-x-2 text-sm">
                    <input
                      type="checkbox"
                      checked={reportData.removedDevice}
                      onChange={e => setReportData(prev => ({ ...prev, removedDevice: e.target.checked }))}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 h-4 w-4"
                    />
                    <span>Removed Device</span>
                  </label>
                  <label className="flex items-center space-x-2 text-sm">
                    <input
                      type="checkbox"
                      checked={reportData.replacementSim}
                      onChange={e => setReportData(prev => ({ ...prev, replacementSim: e.target.checked }))}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 h-4 w-4"
                    />
                    <span>Replacement Sim</span>
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-4 p-3 border border-gray-200 rounded-lg bg-gray-50">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Vehicle No.</label>
                  <input
                    type="text"
                    className="w-full rounded-xl border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                    value={tempVehicleData?.licensePlate || ''}
                    readOnly
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">IMEI No.</label>
                  <input
                    type="text"
                    className="w-full rounded-xl border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                    value={tempVehicleData?.imei ? getDeviceImei(tempVehicleData.imei) : ''}
                    readOnly
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">SIM No.</label>
                  <input
                    type="text"
                    className="w-full rounded-xl border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                    value={tempVehicleData?.sim ? getSimNo(tempVehicleData.sim) : ''}
                    readOnly
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700">Customer Remarks</label>
                <textarea
                  className="w-full rounded-xl border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  rows="2"
                  value={reportData.customerRemarks}
                  onChange={e => setReportData(prev => ({ ...prev, customerRemarks: e.target.value }))}
                  placeholder="Enter customer remarks..."
                />
              </div>

              <div className="mb-4">
                <h4 className="font-bold text-base text-red-600 mb-3">Signatures <span className="text-sm font-normal text-red-500">(All signatures are mandatory)</span></h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="border border-gray-200 rounded-lg p-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Installed By</label>
                    <input
                      type="text"
                      className="w-full rounded-xl border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 mb-2"
                      value={reportData.installerName}
                      onChange={e => setReportData(prev => ({ ...prev, installerName: e.target.value }))}
                      placeholder="Installer name"
                    />
                    <SignaturePad
                      label="Installer Signature"
                      onSave={(data) => handleSignatureSave('installerSignature', data)}
                      onClear={() => handleSignatureClear('installerSignature')}
                      width={280}
                      height={100}
                      required={true}
                    />
                    {reportErrors.installerSignature && (
                      <p className="text-red-500 text-xs mt-1">{reportErrors.installerSignature}</p>
                    )}
                    {reportData.installerSignature && (
                      <div className="mt-2 text-green-600 text-xs flex items-center gap-1">
                        <span>✓</span> Signature saved
                      </div>
                    )}
                  </div>

                  <div className="border border-gray-200 rounded-lg p-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Authorised By Supervisor / Admin</label>
                    <input
                      type="text"
                      className="w-full rounded-xl border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 mb-2"
                      value={reportData.supervisorName}
                      onChange={e => setReportData(prev => ({ ...prev, supervisorName: e.target.value }))}
                      placeholder="Supervisor name"
                    />
                    <SignaturePad
                      label="Supervisor Signature"
                      onSave={(data) => handleSignatureSave('supervisorSignature', data)}
                      onClear={() => handleSignatureClear('supervisorSignature')}
                      width={280}
                      height={100}
                      required={true}
                    />
                    {reportErrors.supervisorSignature && (
                      <p className="text-red-500 text-xs mt-1">{reportErrors.supervisorSignature}</p>
                    )}
                    {reportData.supervisorSignature && (
                      <div className="mt-2 text-green-600 text-xs flex items-center gap-1">
                        <span>✓</span> Signature saved
                      </div>
                    )}
                  </div>

                  <div className="border border-gray-200 rounded-lg p-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Driver</label>
                    <input
                      type="text"
                      className="w-full rounded-xl border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 mb-2"
                      value={reportData.driverName}
                      onChange={e => setReportData(prev => ({ ...prev, driverName: e.target.value }))}
                      placeholder="Driver name"
                    />
                    <SignaturePad
                      label="Driver Signature"
                      onSave={(data) => handleSignatureSave('driverSignature', data)}
                      onClear={() => handleSignatureClear('driverSignature')}
                      width={280}
                      height={100}
                      required={true}
                    />
                    {reportErrors.driverSignature && (
                      <p className="text-red-500 text-xs mt-1">{reportErrors.driverSignature}</p>
                    )}
                    {reportData.driverSignature && (
                      <div className="mt-2 text-green-600 text-xs flex items-center gap-1">
                        <span>✓</span> Signature saved
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap justify-end space-x-3 pt-4 border-t border-gray-200 gap-2">
                <button 
                  type="button" 
                  onClick={handleGoBack} 
                  className="btn-secondary flex items-center gap-2"
                >
                  <FaArrowLeft /> Back
                </button>
                <button 
                  type="button" 
                  onClick={handleReportSkip} 
                  className={`btn-secondary ${isInstaller ? 'opacity-50 cursor-not-allowed' : ''}`}
                  disabled={isInstaller}
                  title={isInstaller ? 'Installers cannot skip the report' : ''}
                >
                  Skip & Save Vehicle
                </button>
                <button type="submit" className="btn-primary">
                  Save Vehicle & Report
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {viewingReport && createPortal(
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[99999] p-2 sm:p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl mx-auto my-4 sm:my-8 max-h-[95vh] sm:max-h-[90vh] overflow-y-auto relative">
            <div className="sticky top-0 bg-white z-10 p-3 sm:p-4 border-b flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-lg sm:text-xl font-bold text-gray-800">Field Call Report (FCR)</h2>
              <div className="flex space-x-2 sm:space-x-3">
                <button
                  type="button"
                  onClick={() => handleDownloadReport(viewingReport)}
                  className="btn-primary flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2"
                >
                  <FaDownload /> Download
                </button>
                <button
                  type="button"
                  onClick={() => setViewingReport(null)}
                  className="btn-secondary text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2"
                >
                  Close
                </button>
              </div>
            </div>

            <div id="fcr-report-content" className="p-3 sm:p-6">
              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-3 sm:gap-6 mb-6">
                <img 
                  src="https://cccs.cordonnx.com/Loginlogo.png" 
                  alt="Cordon Logo" 
                  className="h-12 sm:h-20 w-auto object-contain flex-shrink-0"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <div className="text-center sm:text-left">
                  <h2 className="text-xl sm:text-2xl font-bold">Cordon Telematics Pvt. Ltd.</h2>
                  <h3 className="text-base sm:text-lg font-semibold">Installation report / Field Call Report</h3>
                  <div className="text-sm text-gray-600">
                    No. {viewingReport.reportNumber || viewingReport.report_number || 'N/A'}
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
                  <div className="flex-1 min-w-[200px]">
                    <label className="block text-sm font-medium text-gray-700">Site:</label>
                    <div className="border rounded-lg p-2 mt-1 bg-gray-50 text-sm">
                      {viewingReport.site || '-'}
                    </div>
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <label className="block text-sm font-medium text-gray-700">Branch:</label>
                    <div className="border rounded-lg p-2 mt-1 bg-gray-50 text-sm">
                      {viewingReport.branch || '-'}
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Installation Start Date</label>
                  <div className="border rounded-lg p-2 mt-1 bg-gray-50 text-sm">
                    {viewingReport.installationStartDate || viewingReport.installation_start_date || '-'}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Installation End Date</label>
                  <div className="border rounded-lg p-2 mt-1 bg-gray-50 text-sm">
                    {viewingReport.installationEndDate || viewingReport.installation_end_date || '-'}
                  </div>
                </div>
                <div className="sm:col-span-2 md:col-span-1">
                  <label className="block text-sm font-medium text-gray-700">Billing From Date</label>
                  <div className="border rounded-lg p-2 mt-1 bg-gray-50 text-sm">
                    {viewingReport.billingFromDate || viewingReport.billing_from_date || '-'}
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <h4 className="font-bold text-base sm:text-lg bg-gray-100 p-2 rounded">BEFORE INSTALLATION</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1 sm:gap-2 p-2 border border-gray-200 rounded">
                  {Object.entries(viewingReport.beforeInstallation || viewingReport.before_installation || {}).map(([key, value]) => (
                    <div key={key} className="flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm">
                      <input type="checkbox" checked={value} readOnly className="rounded border-gray-300 h-3 w-3 sm:h-4 sm:w-4" />
                      <span className="truncate">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mb-4">
                <h4 className="font-bold text-base sm:text-lg bg-gray-100 p-2 rounded">AFTER INSTALLATION</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1 sm:gap-2 p-2 border border-gray-200 rounded">
                  {Object.entries(viewingReport.afterInstallation || viewingReport.after_installation || {}).map(([key, value]) => (
                    <div key={key} className="flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm">
                      <input type="checkbox" checked={value} readOnly className="rounded border-gray-300 h-3 w-3 sm:h-4 sm:w-4" />
                      <span className="truncate">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                <div className="flex flex-wrap gap-2 sm:gap-4 border rounded p-2">
                  <div className="flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm">
                    <input type="checkbox" checked={viewingReport.newInstallation || viewingReport.new_installation || false} readOnly className="rounded border-gray-300 h-3 w-3 sm:h-4 sm:w-4" />
                    <span>New Installation</span>
                  </div>
                  <div className="flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm">
                    <input type="checkbox" checked={viewingReport.reInstallation || viewingReport.re_installation || false} readOnly className="rounded border-gray-300 h-3 w-3 sm:h-4 sm:w-4" />
                    <span>Re-Installation</span>
                  </div>
                  <div className="flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm">
                    <input type="checkbox" checked={viewingReport.removedDevice || viewingReport.removed_device || false} readOnly className="rounded border-gray-300 h-3 w-3 sm:h-4 sm:w-4" />
                    <span>Removed Device</span>
                  </div>
                  <div className="flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm">
                    <input type="checkbox" checked={viewingReport.replacementSim || viewingReport.replacement_sim || false} readOnly className="rounded border-gray-300 h-3 w-3 sm:h-4 sm:w-4" />
                    <span>Replacement Sim</span>
                  </div>
                </div>
              </div>

              <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 border rounded p-3 bg-blue-50">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Vehicle No.</label>
                  <div className="mt-1 font-semibold text-gray-900 text-base sm:text-lg">
                    {viewingReport.vehicleNumber || viewingReport.vehicle_number || 
                     (tempVehicleData?.licensePlate) || '-'}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">IMEI No.</label>
                  <div className="mt-1 font-semibold text-gray-900 text-base sm:text-lg">
                    {viewingReport.imei || 
                     (tempVehicleData?.imei ? getDeviceImei(tempVehicleData.imei) : '') || 
                     '-'}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">SIM No.</label>
                  <div className="mt-1 font-semibold text-gray-900 text-base sm:text-lg">
                    {viewingReport.simNo || viewingReport.sim_no ||
                     (tempVehicleData?.sim ? getSimNo(tempVehicleData.sim) : '') || 
                     '-'}
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700">Customer Remarks</label>
                <div className="border rounded-lg p-2 mt-1 bg-gray-50 min-h-[40px] sm:min-h-[60px] text-sm">
                  {viewingReport.customerRemarks || viewingReport.customer_remarks || '-'}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 mb-4 border-t pt-4">
                <div className="border rounded-lg p-3">
                  <label className="block text-sm font-medium text-gray-700">Installed By</label>
                  <div className="border rounded-lg p-2 mt-1 bg-gray-50 text-sm">
                    {viewingReport.installerName || viewingReport.installer_name || '-'}
                  </div>
                  {viewingReport.installerSignature && (
                    <div className="mt-2 border rounded-lg p-2">
                      <img src={viewingReport.installerSignature} alt="Installer signature" className="max-w-full max-h-[40px] sm:max-h-[60px]" />
                    </div>
                  )}
                </div>
                <div className="border rounded-lg p-3">
                  <label className="block text-sm font-medium text-gray-700">Authorised By Supervisor / Admin</label>
                  <div className="border rounded-lg p-2 mt-1 bg-gray-50 text-sm">
                    {viewingReport.supervisorName || viewingReport.supervisor_name || '-'}
                  </div>
                  {viewingReport.supervisorSignature && (
                    <div className="mt-2 border rounded-lg p-2">
                      <img src={viewingReport.supervisorSignature} alt="Supervisor signature" className="max-w-full max-h-[40px] sm:max-h-[60px]" />
                    </div>
                  )}
                </div>
                <div className="border rounded-lg p-3">
                  <label className="block text-sm font-medium text-gray-700">Driver</label>
                  <div className="border rounded-lg p-2 mt-1 bg-gray-50 text-sm">
                    {viewingReport.driverName || viewingReport.driver_name || '-'}
                  </div>
                  {viewingReport.driverSignature && (
                    <div className="mt-2 border rounded-lg p-2">
                      <img src={viewingReport.driverSignature} alt="Driver signature" className="max-w-full max-h-[40px] sm:max-h-[60px]" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {previewImages && createPortal(
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[9999] p-2 sm:p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full p-4 sm:p-6 relative shadow-2xl">
            <button
              type="button"
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 transition z-10 bg-white rounded-full p-2 shadow-lg hover:shadow-xl"
              onClick={handleClosePreview}
            >
              <FaTimes size={20} />
            </button>
            
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">
                Vehicle Images {previewImages.length > 1 && `(${currentImageIndex + 1} of ${previewImages.length})`}
              </h3>
              {previewImages.length > 1 && (
                <span className="text-sm text-gray-500">
                  {currentImageIndex + 1} / {previewImages.length}
                </span>
              )}
            </div>

            <div className="relative">
              <div className="flex items-center justify-center min-h-[200px] sm:min-h-[300px] max-h-[60vh] bg-gray-50 rounded-xl overflow-hidden">
                <img 
                  src={previewImages[currentImageIndex]} 
                  alt={`Vehicle ${currentImageIndex + 1}`} 
                  className="max-w-full max-h-[60vh] object-contain"
                />
              </div>

              {previewImages.length > 1 && (
                <>
                  <button
                    onClick={handlePrevImage}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-gray-700 hover:text-gray-900 p-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 backdrop-blur-sm"
                    aria-label="Previous image"
                  >
                    <FaChevronLeft size={20} />
                  </button>
                  <button
                    onClick={handleNextImage}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-gray-700 hover:text-gray-900 p-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 backdrop-blur-sm"
                    aria-label="Next image"
                  >
                    <FaChevronRight size={20} />
                  </button>
                </>
              )}
            </div>

            {previewImages.length > 1 && (
              <div className="mt-4 flex gap-2 overflow-x-auto pb-2 max-h-20">
                {previewImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImageIndex(idx)}
                    className={`flex-shrink-0 w-12 h-12 sm:w-16 sm:h-16 rounded-lg overflow-hidden border-2 transition-all ${
                      idx === currentImageIndex 
                        ? 'border-indigo-500 shadow-md scale-105' 
                        : 'border-gray-200 hover:border-gray-400'
                    }`}
                  >
                    <img src={img} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default VehicleDetails;